import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  loginUser,
  loginWithGoogle,
  registerUser,
  confirmEmail,
  resendConfirmationCode,
  checkSignupSubdomain,
  resumeSignupDraft,
  resumeSignupPayment,
  forgotPassword,
  resetPassword,
  AuthServiceError,
} from "@shared/services/cog_authService";
import axiosInstance from "@shared/services/api";
import {
  fetchPlans,
  normalizePlansPayload,
  postBilling,
} from "@shared/services/plansBillingApi";
import { openRazorpayCheckout } from "@shared/services/razorpayCheckout";
import "./NovaLanding.css";
import ContactModal from "./ContactModal";
import { brandColors, getSemanticPalette } from "@shared/theme";
import { CONFIG } from "@shared/config";
import { logToServer } from "@shared/utils/logs";
import { getPersonNameError } from "@shared/utils/nameValidation";
import { getEmailError } from "@shared/utils/emailValidation";
import { OTP_VALIDITY_SECONDS, startOtpTimer, getOtpTimer, clearOtpTimer } from "@shared/utils/otpTimer";
import { getPhoneError, OTHER_REGION_CODE } from "@shared/utils/phoneValidation";
import {
  getApiErrorMessage,
  getApiErrorCode,
  getApiErrorExtras,
} from "@shared/utils/apiErrorMessage";
import {
  isOtpExpiredCode,
  isOtpLimitCode,
  isCognitoUnavailable,
  getOtpUserMessage,
} from "@shared/utils/otpErrors";
import {
  getLoginPasswordError,
  getPasswordError,
  PASSWORD_MAX_LENGTH,
} from "@shared/utils/passwordValidation";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ArrowForward from "@mui/icons-material/ArrowForward";
import OtpDigitInput from "@shared/components/OtpDigitInput";

/** Signup name length bounds (stricter than backend PersonName 1–50). */
const SIGNUP_NAME_MIN_LENGTH = 3;
const SIGNUP_NAME_MAX_LENGTH = 30;
/** Organization display name minimum length. */
const SIGNUP_ORG_MIN_LENGTH = 3;
/** Workspace URL prefix length bounds. */
const SIGNUP_SUBDOMAIN_MIN_LENGTH = 2;
const SIGNUP_SUBDOMAIN_MAX_LENGTH = 63;

const landingSemantic = getSemanticPalette(false);

/** User-facing copy for fulfillment 302 ?marketplace_error=CODE redirects. */
const MARKETPLACE_ERROR_MESSAGES = {
  MARKETPLACE_SESSION_EXPIRED:
    "Your AWS Marketplace signup link has expired. Go back to AWS Marketplace, open your subscription (Set up your account), and start signup again. You do not need to subscribe twice.",
  MARKETPLACE_SESSION_INVALID:
    "We could not verify your AWS Marketplace signup. Go back to AWS Marketplace and start signup again.",
  MARKETPLACE_RESOLVE_FAILED:
    "We could not complete your AWS Marketplace registration. Go back to AWS Marketplace and try again. If this keeps happening, contact support.",
  MARKETPLACE_PRODUCT_UNKNOWN:
    "This Marketplace product is not available for signup. Contact support and share your AWS account details.",
  MARKETPLACE_TEMPORARILY_UNAVAILABLE:
    "Signup from AWS Marketplace is temporarily unavailable. Try again in a few minutes, or contact support.",
  MARKETPLACE_ALREADY_LINKED:
    "This AWS Marketplace subscription already has a workspace. Sign in with the email used at signup, or contact support. You do not need to subscribe again.",
};

/** Decode marketplace_session body.exp without verifying the HMAC (omit if past). */
const isMarketplaceSessionUnexpired = (token) => {
  if (!token || typeof token !== "string" || !token.includes(".")) return false;
  try {
    const body = token.split(".")[0];
    const padded = body + "=".repeat((4 - (body.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return typeof payload.exp === "number" && payload.exp > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
};

/** Prefer a still-valid marketplace_session; otherwise omit so Dynamo AWS IDs are used. */
const getActiveMarketplaceSession = (token) =>
  isMarketplaceSessionUnexpired(token) ? token : "";

/** Machine codes from signup_flow.md — route screens with code / next_step. */
const SIGNUP_ERROR_CODES = {
  SUBDOMAIN_TAKEN: "SUBDOMAIN_TAKEN",
  SUBDOMAIN_INVALID: "SUBDOMAIN_INVALID",
  EMAIL_TAKEN: "EMAIL_TAKEN",
  PASSWORD_INVALID: "PASSWORD_INVALID",
  SIGNUP_SESSION_EXPIRED: "SIGNUP_SESSION_EXPIRED",
  SIGNUP_SESSION_INVALID: "SIGNUP_SESSION_INVALID",
  SIGNUP_AUTH_FAILED: "SIGNUP_AUTH_FAILED",
  SIGNUP_LOCKED: "SIGNUP_LOCKED",
  SIGNUP_NOT_FOUND: "SIGNUP_NOT_FOUND",
  SIGNUP_PAYMENT_TOKEN_EXPIRED: "SIGNUP_PAYMENT_TOKEN_EXPIRED",
  EMAIL_ALREADY_CONFIRMED: "EMAIL_ALREADY_CONFIRMED",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  ACCOUNT_READY: "ACCOUNT_READY",
  SIGNUP_PROVISIONING: "SIGNUP_PROVISIONING",
  OTP_INVALID: "OTP_INVALID",
  OTP_EXPIRED: "OTP_EXPIRED",
  OTP_LIMIT: "OTP_LIMIT",
  OTP_FAILED: "OTP_FAILED",
  COGNITO_UNAVAILABLE: "COGNITO_UNAVAILABLE",
  MARKETPLACE_ALREADY_LINKED: "MARKETPLACE_ALREADY_LINKED",
  PROVISION_START_FAILED: "PROVISION_START_FAILED",
  SIGNUP_BACKEND_MISCONFIGURED: "SIGNUP_BACKEND_MISCONFIGURED",
};

/** Map API errors[].field → signup form input name. */
const SIGNUP_FIELD_MAP = {
  email: "email",
  password: "password",
  firstname: "firstName",
  lastname: "lastName",
  tenant_name: "organizationName",
  subdomain: "subdomain",
  region: "region",
  phonenumber: "phoneNumber",
  phone_number: "phoneNumber",
  gstin: "gstin",
  marketplace_session: "marketplace_session",
};

const INITIAL_DEMO_FORM = {
  fullName: "",
  workEmail: "",
  phoneNumber: "",
  companyName: "",
  role: "",
  companySize: "",
  challenge: "",
};

const USE_CASES = [
  { label: "Internal Knowledge", slug: "internal-knowledge" },
  // { label: "Website AI Search", slug: "website-ai-search" },
  // { label: "Dev Onboarding", slug: "dev-onboarding" },
  // { label: "Google Chat Bot", slug: "google-chat-bot" },
];

const RELEASE_NOTES_VERSION = "NOVA 2.0";
const RELEASE_NOTES_DATE = "July 2026";

const RELEASE_NOTES_GLANCE = [
  { title: "Multi-Tenant Platform", summary: "Secure, isolated workspaces for every organization" },
  { title: "Knowledge Base", summary: "Centralized, AI-powered document repository" },
  { title: "Enhanced AI Assistant", summary: "Context-aware responses from org knowledge" },
  { title: "Simplified Onboarding", summary: "Bulk upload, profiles & streamlined invitations" },
  { title: "Enterprise Security", summary: "Auth, RBAC, session & tenant isolation" },
  { title: "Communication Experience", summary: "Professional email templates & notifications" },
];

const RELEASE_NOTES_FEATURES = [
  {
    title: "Multi-Tenant Platform",
    intro:
      "NOVA now supports a Multi-Tenant Architecture, enabling multiple organizations to securely operate on a shared platform while maintaining complete isolation of their users, data, and organizational knowledge.",
    bullets: [
      "Dedicated workspace for every organization",
      "Organization-specific AI knowledge base",
      "Independent employee and administrator management",
      "Secure tenant-level data isolation",
      "Enterprise-ready and highly scalable architecture",
    ],
  },
  {
    title: "Knowledge Base",
    intro:
      "NOVA introduces a powerful Knowledge Base that enables organizations to centrally manage and organize their information, allowing employees to receive accurate AI-powered responses based on approved company content.",
    bullets: [
      "Centralized repository for organizational knowledge",
      "Categorized documents for easy navigation and management",
      "Support for HR Policies, Compliance, SOPs, Training Materials, Process Documents, Technical Documentation, FAQs, and more",
      "Document version management",
      "Active and inactive document management",
      "Simplified document upload and maintenance",
      "AI-powered retrieval from the latest approved documents",
    ],
  },
  {
    title: "Enhanced AI Assistant",
    intro:
      "NOVA provides a more intelligent and context-aware conversational experience by leveraging your organization's knowledge base.",
    bullets: [
      "Organization-specific AI responses",
      "Improved contextual understanding",
      "Enhanced conversation history",
      "Faster retrieval of relevant information",
      "More accurate responses based on approved company knowledge",
    ],
  },
  {
    title: "Simplified Employee Onboarding",
    intro:
      "Employee onboarding has been redesigned to provide a faster, smoother, and more efficient experience.",
    bullets: [
      "Improved employee invitation workflow",
      "Bulk employee upload for faster onboarding",
      "Employee profiles now capture Personal Details",
      "Enhanced account activation experience",
      "Professional onboarding email communications",
      "Simplified employee provisioning and user management",
    ],
  },
  {
    title: "Enterprise Security & Access Control",
    intro: "NOVA continues to strengthen enterprise security with enhanced access management.",
    bullets: [
      "Secure authentication",
      "Tenant-based access control",
      "Role-based permissions",
      "Improved session management",
      "Secure user provisioning",
    ],
  },
  {
    title: "Enhanced Communication Experience",
    intro: "Platform communications have been redesigned to provide a more professional and consistent experience.",
    bullets: [
      "Redesigned invitation emails",
      "Improved onboarding notifications",
      "Professional email templates",
      "Reliable email delivery",
    ],
  },
];

const RELEASE_NOTES_COMING_SOON = [
  {
    title: "GitHub Integration",
    summary:
      "Connect GitHub repositories with NOVA to search source code, documentation, issues, and pull requests using natural language.",
  },
  {
    title: "Google Chat Integration",
    summary:
      "Access NOVA directly from Google Chat to retrieve organizational knowledge and interact with the AI assistant without leaving your workspace.",
  },
  {
    title: "Embedded Widgets",
    summary:
      "Embed NOVA into your intranet, employee portals, or business applications, enabling employees to access organizational knowledge wherever they work.",
  },
  {
    title: "Row-Level Security (RLS) Integration",
    summary:
      "Enable fine-grained, role-based access controls to ensure users only access the information they are authorized to view, strengthening enterprise security and governance.",
  },
  {
    title: "Expanded Knowledge Base",
    summary:
      "Expand the organizational knowledge base with richer content, additional document types, and broader categorization to deliver even more comprehensive AI-powered responses.",
  },
];

const REGION_OPTIONS = [
  { code: "US", name: "United States", dialCode: "+1" },
  { code: "IN", name: "India", dialCode: "+91" },
  { code: "GB", name: "United Kingdom", dialCode: "+44" },
  { code: "CA", name: "Canada", dialCode: "+1" },
  { code: "AU", name: "Australia", dialCode: "+61" },
  { code: "DE", name: "Germany", dialCode: "+49" },
  { code: "FR", name: "France", dialCode: "+33" },
  { code: "SG", name: "Singapore", dialCode: "+65" },
  { code: "AE", name: "United Arab Emirates", dialCode: "+971" },
  { code: "MY", name: "Malaysia", dialCode: "+60" },
  // { code: OTHER_REGION_CODE, name: "Others (none of the above)", dialCode: "" },
];

const TENANT_ROOT_DOMAIN = (
  CONFIG.REACT_APP_TENANT_ROOT_DOMAIN || "nova.bizacuity.ai"
).trim();
const TENANT_ROOT_DOMAINS = [
  TENANT_ROOT_DOMAIN,
  "nova.dev.bizacuity.ai",
  "nova.bizacuity.ai",
].filter((root, index, roots) => root && roots.indexOf(root) === index)
  .sort((left, right) => right.length - left.length);

const normalizeSubdomainInput = (rawValue) => {
  let value = (rawValue || "").trim().toLowerCase();
  value = value.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  for (const root of TENANT_ROOT_DOMAINS) {
    const suffix = `.${root}`;
    if (value.endsWith(suffix)) {
      value = value.slice(0, -suffix.length);
      break;
    }
    if (value === root) {
      value = "";
      break;
    }
  }
  return value.replace(/\s/g, "");
};

/**
 * Restricts signup phone input to an optional leading "+" and digits only.
 * Strips spaces, letters, and punctuation so values stay E.164-like (e.g. +9198…).
 */
const sanitizeSignupPhoneInput = (rawValue) => {
  const raw = String(rawValue || "");
  const hasPlus = raw.trimStart().startsWith("+");
  const digits = raw.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
};

/** Looks up a REGION_OPTIONS row by ISO / OTHER code. */
const getSignupRegionOption = (regionCode) =>
  REGION_OPTIONS.find((r) => r.code === normalizeRegionCode(regionCode));

/**
 * Returns national digits only, stripping the locked dial code for a listed
 * country. Others (and unknown) keep digits with no country prefix.
 */
const getSignupNationalDigits = (phone, regionCode) => {
  const current = sanitizeSignupPhoneInput(phone);
  const region = getSignupRegionOption(regionCode);
  if (region?.dialCode && current.startsWith(region.dialCode)) {
    return current.slice(region.dialCode.length);
  }
  return current.replace(/^\+/, "");
};

/**
 * Builds the stored phone value: locked dial code + national digits for a
 * listed country, or digits only (no dial code) for Others / no region.
 * If the user pastes a number that already includes the dial digits, those
 * are stripped so the prefix is never duplicated.
 */
const composeSignupPhoneNumber = (nationalRaw, regionCode) => {
  const region = getSignupRegionOption(regionCode);
  let national = String(nationalRaw || "").replace(/\D/g, "");
  if (region?.dialCode) {
    const dialDigits = region.dialCode.replace(/\D/g, "");
    if (dialDigits && national.startsWith(dialDigits)) {
      national = national.slice(dialDigits.length);
    }
    return `${region.dialCode}${national}`;
  }
  return national;
};

/**
 * Normalizes a raw region value onto the uppercase ISO codes REGION_OPTIONS
 * uses. The backend/session draft can round-trip region in lowercase, but
 * the <select>'s option values (and REGION_OPTIONS.find lookups) are
 * uppercase-only.
 */
const normalizeRegionCode = (rawValue) => String(rawValue || "").trim().toUpperCase();

/**
 * Validates workspace URL prefix format (3–20 chars, letters/numbers/hyphens).
 * Does not check availability — that is handled by checkSignupSubdomain.
 */
const validateSubdomain = (rawValue) => {
  const value = normalizeSubdomainInput(rawValue);
  if (!value) return "URL prefix is required.";
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) || value.length < SIGNUP_SUBDOMAIN_MIN_LENGTH) {
    return "Use only letters, numbers, and hyphens for your workspace URL.";
  }
  if (value.length > SIGNUP_SUBDOMAIN_MAX_LENGTH) {
    return "Use only letters, numbers, and hyphens for your workspace URL.";
  }
  return "";
};

/**
 * Returns a user-facing error for organization name, or "" when valid.
 * Required and at least SIGNUP_ORG_MIN_LENGTH characters after trim.
 */
const getOrganizationNameError = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "Organization name is required";
  if (trimmed.length < SIGNUP_ORG_MIN_LENGTH) {
    return `Organization name must be at least ${SIGNUP_ORG_MIN_LENGTH} characters.`;
  }
  return "";
};

const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const normalizeGstin = (value) => String(value || "").trim().toUpperCase();

const getGstinError = (value) => {
  const trimmed = normalizeGstin(value);
  if (!trimmed) return "GSTIN is required";
  if (!GSTIN_PATTERN.test(trimmed)) {
    return "Enter a valid 15-character GSTIN, e.g. 22AAAAA0000A1Z5.";
  }
  return "";
};

const SIGNUP_NAME_LENGTH_OPTS = {
  minLength: SIGNUP_NAME_MIN_LENGTH,
  maxLength: SIGNUP_NAME_MAX_LENGTH,
};

/**
 * Validates a single signup details field against current form values.
 * Used on blur and as building blocks for full-form validation.
 */
const validateSignupField = (fieldName, form = {}) => {
  switch (fieldName) {
    case "firstName":
      return getPersonNameError(form.firstName, "First name", SIGNUP_NAME_LENGTH_OPTS);
    case "lastName":
      return getPersonNameError(form.lastName, "Last name", SIGNUP_NAME_LENGTH_OPTS);
    case "organizationName":
      return getOrganizationNameError(form.organizationName);
    case "subdomain":
      return validateSubdomain(form.subdomain);
    case "region":
      return form.region ? "" : "Please select your region.";
    case "email":
      return getEmailError(form.email, "Email");
    case "phoneNumber": {
      const region = REGION_OPTIONS.find((r) => r.code === form.region);
      return getPhoneError(
        form.phoneNumber,
        form.region,
        region?.dialCode || "",
        { required: true },
      );
    }
    case "password":
      return getPasswordError(form.password, "Password");
    case "confirmPassword": {
      if (!String(form.confirmPassword || "").length) {
        return "Confirm password is required";
      }
      if (form.password !== form.confirmPassword) {
        return "Passwords do not match.";
      }
      return "";
    }
    default:
      return "";
  }
};

/**
 * Validates every signup details field. Returns a map of fieldName → message
 * for invalid fields only (empty object means the form is ready to submit).
 */
const validateSignupForm = (form = {}) => {
  const fields = [
    "firstName",
    "lastName",
    "organizationName",
    "subdomain",
    "region",
    "email",
    "phoneNumber",
    "password",
    "confirmPassword",
  ];
  const errors = {};
  fields.forEach((name) => {
    const message = validateSignupField(name, form);
    if (message) errors[name] = message;
  });
  return errors;
};

const PENDING_SIGNUP_KEY = "pendingSignup";
const SIGNUP_DRAFT_KEY = "novaSignupDraft";
const PROVISIONING_WINDOW_MS = 30 * 60 * 1000;
/** Cognito's confirm lock outlasts a few minutes; hold the buttons so retries do not extend it. */
const SIGNUP_OTP_LOCKOUT_MS = 15 * 60 * 1000;
/** Signup OTP is shown valid for the shared OTP window; Resend stays disabled until it ends. */
const SIGNUP_OTP_RESEND_COOLDOWN_MS = OTP_VALIDITY_SECONDS * 1000;

/**
 * When Resend may be used, from the server send time.
 * A missing or unparseable time starts a fresh window, matching a new send.
 */
const signupResendAvailableFromSentAt = (otpSentAt) => {
  const raw = String(otpSentAt || "").trim();
  const sent = Date.parse(raw);
  if (!raw || Number.isNaN(sent)) {
    return Date.now() + SIGNUP_OTP_RESEND_COOLDOWN_MS;
  }
  return sent + SIGNUP_OTP_RESEND_COOLDOWN_MS;
};

/** Format remaining seconds as mm:ss for OTP countdown text. */
const formatOtpCountdown = (totalSeconds) => {
  const safe = Math.max(0, totalSeconds);
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
};

/** Persist signup funnel snapshot (no password). Includes signup_id / session / payment tokens. */
const writeSignupDraft = (form, selectedPlan, plansByName, extra = {}) => {
  try {
    const plan = plansByName?.[selectedPlan];
    const price = plan?.prices?.[0];
    sessionStorage.setItem(
      SIGNUP_DRAFT_KEY,
      JSON.stringify({
        firstName: form.firstName || "",
        lastName: form.lastName || "",
        email: (form.email || "").trim().toLowerCase(),
        region: normalizeRegionCode(form.region),
        phoneNumber: form.phoneNumber || "",
        subdomain: normalizeSubdomainInput(form.subdomain || ""),
        organizationName: form.organizationName || "",
        selectedPlan: selectedPlan || "starter",
        plan_id: plan?.id ?? null,
        price_id: price?.id ?? null,
        signupId: extra.signupId ?? "",
        signupSessionToken: extra.signupSessionToken ?? "",
        signupPaymentToken: extra.signupPaymentToken ?? "",
        tenantSlug: extra.tenantSlug ?? normalizeSubdomainInput(form.subdomain || ""),
        locked: Boolean(extra.locked),
        ...extra,
      }),
    );
  } catch {
    // ignore quota / private mode
  }
};

const readSignupDraft = () => {
  try {
    const raw = sessionStorage.getItem(SIGNUP_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearSignupDraft = () => {
  try {
    sessionStorage.removeItem(SIGNUP_DRAFT_KEY);
  } catch {
    // ignore
  }
};

/** Clear cross-refresh wait hint written after pay/activate (localStorage). */
const clearPendingSignup = () => {
  try {
    localStorage.removeItem(PENDING_SIGNUP_KEY);
  } catch {
    // ignore
  }
};

/** Empty signup form shape — used when abandoning or dismissing wait UI. */
const EMPTY_SIGNUP_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  region: "",
  phoneNumber: "",
  subdomain: "",
  password: "",
  confirmPassword: "",
  organizationName: "",
  gstin: "",
};

/**
 * Wait-modal variants:
 * - payment_success: just completed checkout/activate
 * - already_provisioning: API/URL/pending hint that setup is already running
 */
const PROVISIONING_VARIANT = {
  PAYMENT_SUCCESS: "payment_success",
  ALREADY_PROVISIONING: "already_provisioning",
};

/** Profile fields required before OTP (password is not persisted in the draft). */
const isSignupProfileComplete = (form = {}) =>
  Boolean(
    String(form.firstName || "").trim() &&
      String(form.lastName || "").trim() &&
      String(form.email || "").trim() &&
      String(form.organizationName || "").trim() &&
      String(form.subdomain || "").trim() &&
      String(form.region || "").trim(),
  );

const INCOMPLETE_SIGNUP_MESSAGE =
  "Incomplete data. Please fill in all required fields.";

const buildProvisioningDomain = (subdomain) => {
  const normalized = normalizeSubdomainInput(subdomain || "");
  return normalized ? `${normalized}.${TENANT_ROOT_DOMAIN}` : "";
};

/**
 * Domain for an already-bound email (resume / provisioning), from API extras only.
 * Never use the form URL prefix — that can differ from the tenant the email is on.
 * Returns "" when unknown so the wait modal hides "Expected domain".
 */
const resolveBoundTenantDomain = (extras = {}) => {
  const hostRaw = String(extras.tenant_host || "").trim();
  if (hostRaw) {
    return hostRaw
      .replace(/^https?:\/\//i, "")
      .replace(/\/.*$/, "")
      .toLowerCase();
  }
  const slug = normalizeSubdomainInput(
    extras.tenant_slug || extras.subdomain || "",
  );
  return slug ? buildProvisioningDomain(slug) : "";
};

const readPendingSignup = () => {
  try {
    const raw = localStorage.getItem(PENDING_SIGNUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isActivePendingProvisioning = (pending) => {
  if (pending?.type !== "signup" || !pending?.email?.trim() || !pending?.confirmedAt) {
    return false;
  }

  const confirmedAtMs = new Date(pending.confirmedAt).getTime();
  if (!Number.isFinite(confirmedAtMs)) {
    return false;
  }

  return Date.now() - confirmedAtMs < PROVISIONING_WINDOW_MS;
};

const shouldClearPendingSignup = (pending) => {
  if (!pending || pending.type !== "signup") {
    return false;
  }

  if (!pending.confirmedAt) {
    return true;
  }

  return !isActivePendingProvisioning(pending);
};

// Display-only symbol per currency code — no conversion happens here. The
// actual amount and currency always come straight from the DB (plan_prices,
// via the /plans lambda endpoint), so this page never drifts from what the
// app actually charges.
const CURRENCY_SYMBOLS = {
  USD: "$",
  INR: "₹",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  SGD: "S$",
  AED: "AED ",
  MYR: "RM",
};

const formatPriceAmount = (amount, currencyCode) => {
  const truncated = Math.floor(amount);
  if (currencyCode === "INR") {
    return truncated.toLocaleString("en-IN");
  }
  return truncated.toLocaleString("en-US");
};

const formatStorageLimit = (numBytes) => {
  if (numBytes == null) return "Unlimited storage";
  const n = Number(numBytes);
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB storage`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(0)} MB storage`;
  return `${(n / 1024 ** 3).toFixed(0)} GB storage`;
};

const formatUsersLimit = (users) =>
  users == null ? "Unlimited users" : `Up to ${Number(users).toLocaleString()} users`;

const formatMessagesLimit = (aiMessages) =>
  aiMessages == null ? "Unlimited AI messages" : `${Number(aiMessages).toLocaleString()} AI messages / month`;

// `expiresAt` is an absolute Date.now()-style timestamp (persisted across
// page refreshes), not a relative duration — so remounting this component
// (e.g. after a reload) recomputes the true remaining time instead of
// restarting the countdown from the full duration.
function CountdownTimer({ expiresAt, onComplete }) {
  const computeRemaining = () => Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
  const [timeLeft, setTimeLeft] = useState(computeRemaining);

  useEffect(() => {
    setTimeLeft(computeRemaining());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);

  useEffect(() => {
    if (timeLeft <= 0) {
      onComplete();
      return;
    }
    const interval = setInterval(() => setTimeLeft(computeRemaining()), 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, expiresAt, onComplete]);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const secs = timeLeft % 60;

  const formattedTime =
    hours > 0
      ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
      : `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;

  return (
    <p style={{ fontSize: "13px", color: landingSemantic.text.muted, textAlign: "center", margin: "8px 0 0" }}>
      Code expires in{" "}
      <strong style={{ color: timeLeft <= 30 ? brandColors.error : landingSemantic.text.label }}>
        {formattedTime}
      </strong>
    </p>
  );
}

/** Hide/Show control matching the employee login password fields. */
function AuthPasswordToggle({ show, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      className="password-toggle-btn"
      onClick={onClick}
      disabled={disabled}
      title={show ? "Hide password" : "Show password"}
      aria-label={show ? "Hide password" : "Show password"}
      data-testid={testId}
    >
      {show ? (
        <VisibilityOff sx={{ fontSize: 18 }} />
      ) : (
        <Visibility sx={{ fontSize: 18 }} />
      )}
    </button>
  );
}

/** Two-step indicator for forgot-password, matching employee Login.jsx. */
function ResetStepper({ activeStep }) {
  const steps = ["Email", "Reset Password"];
  return (
    <div className="reset-stepper" aria-label="Reset password steps">
      {steps.map((label, index) => (
        <div key={label} className="reset-stepper-item">
          {index > 0 ? (
            <span
              className={[
                "reset-stepper-connector",
                index <= activeStep ? "completed" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          ) : null}
          <div
            className={[
              "reset-stepper-step",
              index === activeStep ? "active" : "",
              index < activeStep ? "completed" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <span className="reset-stepper-index">{index + 1}</span>
            <span className="reset-stepper-label">{label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function NovaLanding({
  initialAuthModal,
  initialSignInEmail = "",
  initialSignInMessage = "",
  marketplaceSession: marketplaceSessionProp = "",
  marketplaceError: marketplaceErrorProp = "",
  marketplaceReturning = false,
  signupNext = "",
} = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navShadow, setNavShadow] = useState(false);
  const [plansByName, setPlansByName] = useState(null); // {starter, professional, enterprise} from /plans lambda endpoint

  const professionalPrice = plansByName?.professional?.prices?.[0];
  const priceData = useMemo(() => {
    if (!professionalPrice) return { symbol: "", price: "—", label: "" };
    const currency = professionalPrice.currency || "INR";
    return {
      symbol: CURRENCY_SYMBOLS[currency] || `${currency} `,
      price: formatPriceAmount(Number(professionalPrice.price) || 0, currency),
      label: currency,
    };
  }, [professionalPrice]);

  const starterLimits = plansByName?.starter?.limits;
  const professionalLimits = plansByName?.professional?.limits;
  const professionalUsersLimit = professionalLimits?.users ?? null;
  const perUserPriceDisplay = useMemo(() => {
    if (!professionalPrice || !professionalUsersLimit) return null;
    const currency = professionalPrice.currency || "INR";
    const perUser = (Number(professionalPrice.price) || 0) / professionalUsersLimit;
    return formatPriceAmount(perUser, currency);
  }, [professionalPrice, professionalUsersLimit]);
  // 18% GST applies to the Professional plan — shown upfront here so the
  // Razorpay charge (see billing_service.apply_gst_if_applicable on the
  // backend) never surprises the payer at checkout.
  const professionalGstTotalDisplay = useMemo(() => {
    if (!professionalPrice) return null;
    const currency = professionalPrice.currency || "INR";
    const total = (Number(professionalPrice.price) || 0) * 1.18;
    return formatPriceAmount(total, currency);
  }, [professionalPrice]);
  const enterprisePrice = plansByName?.enterprise?.prices?.[0];
  const isEnterprisePayPerUse = enterprisePrice?.billing_cycle === "pay_per_use";
  // Hide a pricing card only once we know for certain (from subscription_plans.is_active)
  // that the plan is disabled — while plans are still loading, keep showing the fallback card.
  const isStarterPlanActive = plansByName?.starter?.is_active !== false;
  const isProfessionalPlanActive = plansByName?.professional?.is_active !== false;
  const isEnterprisePlanActive = plansByName?.enterprise?.is_active !== false;
  const [pricingAlert, setPricingAlert] = useState({ tier: "", type: "", message: "" });
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showProvisioningModal, setShowProvisioningModal] = useState(false);
  const [provisioningDomain, setProvisioningDomain] = useState("");
  const [provisioningEmail, setProvisioningEmail] = useState("");
  /** Distinguishes just-paid vs already-provisioning wait copy. */
  const [provisioningVariant, setProvisioningVariant] = useState(
    PROVISIONING_VARIANT.PAYMENT_SUCCESS,
  );
  /** Optional API message shown on the wait modal (verbatim). */
  const [provisioningMessage, setProvisioningMessage] = useState("");
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [signInForm, setSignInForm] = useState({ email: "", password: "" });
  const [passwordResetStep, setPasswordResetStep] = useState("signin");
  const [passwordResetForm, setPasswordResetForm] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [signInError, setSignInError] = useState("");
  const [signInSuccess, setSignInSuccess] = useState("");
  const [signUpForm, setSignUpForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    region: "",
    phoneNumber: "",
    subdomain: "",
    password: "",
    confirmPassword: "",
    organizationName: "",
    gstin: "",
  });
  const [hasGstin, setHasGstin] = useState(false);
  const [gstinError, setGstinError] = useState("");
  const [signUpStep, setSignUpStep] = useState("details");
  const [signUpOtp, setSignUpOtp] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [signupOrder, setSignupOrder] = useState(null);
  // Set once Razorpay reports a captured payment — kept around so a failed
  // /billing/razorpay/verify or /confirm-email (e.g. a network blip) can be
  // retried without re-charging the card.
  const [paidSignupResponse, setPaidSignupResponse] = useState(null);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpFeedback, setSignUpFeedback] = useState({
    type: "",
    message: "",
  });
  const [subdomainError, setSubdomainError] = useState("");
  const [signUpEmailError, setSignUpEmailError] = useState("");
  /** Field-level highlights from errors[].field (signup_flow.md). */
  const [signupFieldErrors, setSignupFieldErrors] = useState({});
  const [resumePaymentForm, setResumePaymentForm] = useState({
    email: "",
    password: "",
  });
  const [showResumePassword, setShowResumePassword] = useState(false);
  const [demoForm, setDemoForm] = useState(INITIAL_DEMO_FORM);
  const [demoErrors, setDemoErrors] = useState({});
  const [demoFeedback, setDemoFeedback] = useState({ type: "", message: "" });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);
  const [showRefundPolicyModal, setShowRefundPolicyModal] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showSignUpConfirmPassword, setShowSignUpConfirmPassword] =
    useState(false);
  const [resetOtpExpired, setResetOtpExpired] = useState(false);
  const [resetOtpExpiresAt, setResetOtpExpiresAt] = useState(null);

  // After a refresh, bring back the password-reset OTP step while its 3-minute timer is still running.
  useEffect(() => {
    const active = getOtpTimer("reset");
    if (!active) return;
    setPasswordResetForm((prev) => ({ ...prev, email: active.email || prev.email }));
    setPasswordResetStep("reset");
    setResetOtpExpiresAt(active.expiresAt);
    setShowPasswordResetModal(true);
  }, []);
  /** Epoch ms until Verify/Resend stay disabled after Cognito OTP_LIMIT. */
  const [signUpOtpLockedUntil, setSignUpOtpLockedUntil] = useState(null);
  /** Epoch ms until Resend is allowed again after a code was just sent. */
  const [signUpResendAvailableAt, setSignUpResendAvailableAt] = useState(null);
  const [signupOtpClock, setSignupOtpClock] = useState(() => Date.now());
  /** SignupSessions continuity tokens from signup_flow.md. */
  const [signupId, setSignupId] = useState("");
  const [signupSessionToken, setSignupSessionToken] = useState("");
  const [signupPaymentToken, setSignupPaymentToken] = useState("");
  const [signupLocked, setSignupLocked] = useState(false);
  /** True only after a successful create/update signup submit for the current form. */
  const [detailsConfirmedForOtp, setDetailsConfirmedForOtp] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [subdomainChecking, setSubdomainChecking] = useState(false);
  const subdomainCheckTimerRef = useRef(null);
  /** Prevents double auto-checkout when entering the payment step. */
  const checkoutAutoStartedRef = useRef(false);
  /** Prevents double confirm-email when Verify is clicked repeatedly. */
  const signUpOtpVerifyingRef = useRef(false);
  /** Prevents double reset-password submit. */
  const resetPasswordSubmittingRef = useRef(false);
  const [marketplaceSession, setMarketplaceSession] = useState(() => {
    if (marketplaceSessionProp) return marketplaceSessionProp;
    try {
      return sessionStorage.getItem("marketplace_session") || "";
    } catch {
      return "";
    }
  });
  const revealRefs = useRef([]);
  const highlightTimerRef = useRef(null);
  const [highlightedFeature, setHighlightedFeature] = useState("");
  const location = useLocation();

  const signupOtpLocked = (signUpOtpLockedUntil || 0) > signupOtpClock;
  const signupResendCooling = (signUpResendAvailableAt || 0) > signupOtpClock;
  // A stored send deadline that is no longer in the future means the code is spent.
  const signupCodeExpired =
    signUpResendAvailableAt != null && !signupResendCooling && !signupOtpLocked;

  // Tick while a signup OTP lock or resend pause is active so the buttons re-enable on time.
  // When the deadline has already passed, snap the clock forward. A stale clock
  // would keep Resend disabled after the server has expired the code.
  useEffect(() => {
    const until = Math.max(signUpOtpLockedUntil || 0, signUpResendAvailableAt || 0);
    const now = Date.now();
    if (until <= now) {
      setSignupOtpClock(now);
      return undefined;
    }
    setSignupOtpClock(now);
    const id = setInterval(() => setSignupOtpClock(Date.now()), 1000);
    return () => clearInterval(id);
  }, [signUpOtpLockedUntil, signUpResendAvailableAt]);

  useEffect(() => {
    if (!marketplaceSessionProp) return;
    setMarketplaceSession(marketplaceSessionProp);
    try {
      sessionStorage.setItem("marketplace_session", marketplaceSessionProp);
    } catch {
      /* ignore */
    }
  }, [marketplaceSessionProp]);

  /** Fulfillment failures land on /signup?marketplace_error=CODE — show catalog copy. */
  useEffect(() => {
    const code = (marketplaceErrorProp || "").trim().toUpperCase();
    if (!code) return;
    const message =
      MARKETPLACE_ERROR_MESSAGES[code] ||
      "We could not complete your AWS Marketplace signup. Go back to AWS Marketplace and try again.";
    try {
      sessionStorage.removeItem("marketplace_session");
    } catch {
      /* ignore */
    }
    setMarketplaceSession("");
    setShowSignUpModal(true);
    setShowSignInModal(false);
    setShowProvisioningModal(false);
    setSignUpStep("details");
    setSignUpFeedback({ type: "error", message });
  }, [marketplaceErrorProp]);

  /**
   * URL bootstrap (§2): returning Marketplace buyers with next=payment|wait_provisioning.
   * Do not open a fresh editable signup form for next=payment.
   */
  useEffect(() => {
    if (!marketplaceReturning && !signupNext) return;

    if (signupNext === "wait_provisioning") {
      const draft = readSignupDraft();
      setShowSignUpModal(false);
      setShowSignInModal(false);
      setShowProvisioningModal(true);
      setProvisioningVariant(PROVISIONING_VARIANT.ALREADY_PROVISIONING);
      setProvisioningMessage("");
      if (draft?.email) {
        setProvisioningEmail(draft.email);
        setProvisioningDomain(buildProvisioningDomain(draft.subdomain));
      }
      setSignUpFeedback({ type: "", message: "" });
      return;
    }

    if (signupNext === "payment") {
      const draft = readSignupDraft();
      setShowSignUpModal(true);
      setShowSignInModal(false);
      setShowProvisioningModal(false);
      setSignupLocked(true);
      setSignUpStep("resume-payment");
      setSignupOrder(null);
      setPaidSignupResponse(null);
      if (draft?.email) {
        setResumePaymentForm((prev) => ({
          ...prev,
          email: draft.email,
        }));
        setSignUpForm((prev) => ({
          ...prev,
          email: draft.email || prev.email,
          subdomain: draft.subdomain || prev.subdomain,
          organizationName: draft.organizationName || prev.organizationName,
          firstName: draft.firstName || prev.firstName,
          lastName: draft.lastName || prev.lastName,
        }));
      }
      setSignUpFeedback({
        type: "success",
        message:
          "Your email is already verified. Enter the password you used when you signed up to continue to payment.",
      });
      return;
    }

    if (marketplaceReturning && initialAuthModal === "signup" && !signupNext) {
      // marketplace=1 without next — still show signup; session may be separate.
      setShowSignUpModal(true);
      setShowSignInModal(false);
    }
  }, [marketplaceReturning, signupNext, initialAuthModal]);

  const clearSignupFieldErrors = () => setSignupFieldErrors({});

  /** Highlight form inputs from API errors[].field list. */
  const applySignupFieldErrors = (error) => {
    const rawErrors =
      error?.response?.data?.errors ||
      (error instanceof AuthServiceError ? error.extras?.errors : null) ||
      [];
    const source = Array.isArray(rawErrors) ? rawErrors : [];
    const next = {};
    source.forEach((item) => {
      const apiField = (item?.field || "").toLowerCase();
      const formKey = SIGNUP_FIELD_MAP[apiField];
      if (formKey && formKey !== "marketplace_session") {
        next[formKey] = item.message || error.message || "Invalid value";
      }
    });
    const extraField = (
      error?.extras?.field ||
      error?.response?.data?.errors?.[0]?.field ||
      ""
    ).toLowerCase();
    if (extraField && SIGNUP_FIELD_MAP[extraField]) {
      const formKey = SIGNUP_FIELD_MAP[extraField];
      if (formKey !== "marketplace_session" && !next[formKey]) {
        next[formKey] =
          (error instanceof AuthServiceError && error.message) ||
          "Invalid value";
      }
    }
    setSignupFieldErrors(next);
    if (next.subdomain) {
      setSubdomainError(next.subdomain);
      setSubdomainAvailable(false);
    }
    return next;
  };

  const signupInputStyle = (fieldName) =>
    Boolean(
      signupFieldErrors[fieldName] ||
        (fieldName === "subdomain" && subdomainError),
    )
      ? { borderColor: "#dc2626", boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.15)" }
      : undefined;

  /** True when every details-field rule passes (used to enable Verify Email). */
  const isSignupDetailsValid = useMemo(
    () => Object.keys(validateSignupForm(signUpForm)).length === 0,
    [signUpForm],
  );
  const signupDialCode = getSignupRegionOption(signUpForm.region)?.dialCode || "";
  const signupPhoneNational = getSignupNationalDigits(
    signUpForm.phoneNumber,
    signUpForm.region,
  );

  useEffect(
    () => () => {
      if (subdomainCheckTimerRef.current) {
        clearTimeout(subdomainCheckTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (!hash.startsWith("feat-")) {
      setHighlightedFeature("");
      return undefined;
    }

    const slug = hash.slice("feat-".length);
    const target = document.getElementById(hash);
    if (!target) return undefined;

    const scrollTimer = window.setTimeout(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setHighlightedFeature(slug);
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = window.setTimeout(() => {
        setHighlightedFeature("");
      }, 3500);
    }, 150);

    return () => {
      clearTimeout(scrollTimer);
    };
  }, [location.hash]);

  useEffect(() => {
    if (initialAuthModal === "signin") {
      setShowSignInModal(true);
      setShowSignUpModal(false);
      setShowProvisioningModal(false);
      setSignInSuccess(initialSignInMessage);
      if (initialSignInEmail) {
        setSignInForm((prev) => ({
          ...prev,
          email: initialSignInEmail,
        }));
      }
      return;
    }

    if (initialAuthModal === "signup") {
      // Returning Marketplace buyers: bootstrap (§2) owns the screen — do not
      // reset to a fresh editable form when next=payment|wait_provisioning.
      if (
        signupNext === "payment" ||
        signupNext === "wait_provisioning"
      ) {
        return;
      }
      setShowSignUpModal(true);
      setSignUpStep("details");
      setSignUpOtp("");
      setSelectedPlan("starter");
      setSignupOrder(null);
      setPaidSignupResponse(null);
      setSignupId("");
      setSignupSessionToken("");
      setSignupPaymentToken("");
      setSignupLocked(false);
      setSubdomainAvailable(null);
      setSignUpFeedback({ type: "", message: "" });
      setShowSignInModal(false);
      setShowProvisioningModal(false);
    }
  }, [
    initialAuthModal,
    initialSignInEmail,
    initialSignInMessage,
    signupNext,
  ]);

  useEffect(() => {
    if (!marketplaceSession) return;
    setShowSignUpModal(true);
    setShowSignInModal(false);
  }, [marketplaceSession]);

  useEffect(() => {
    // URL Marketplace bootstrap wins over local draft restore.
    if (signupNext === "payment" || signupNext === "wait_provisioning") {
      return;
    }

    // URL Marketplace bootstrap wins over local draft restore.
    if (signupNext === "payment" || signupNext === "wait_provisioning") {
      return;
    }

    const pending = readPendingSignup();
    if (isActivePendingProvisioning(pending)) {
      setProvisioningEmail((pending.email || "").trim().toLowerCase());
      setProvisioningDomain(buildProvisioningDomain(pending.subdomain));
      setProvisioningVariant(PROVISIONING_VARIANT.ALREADY_PROVISIONING);
      setProvisioningMessage("");
      setShowSignUpModal(false);
      setShowSignInModal(false);
      setShowProvisioningModal(true);
      return;
    }

    if (shouldClearPendingSignup(pending)) {
      clearPendingSignup();
    }

    // Restore in-progress signup (OTP / payment) after refresh.
    const draft = readSignupDraft();
    if (!draft?.email || !draft?.subdomain) return;
    if (draft.step !== "otp" && draft.step !== "payment") return;

    const restoredForm = {
      firstName: draft.firstName || "",
      lastName: draft.lastName || "",
      email: draft.email || "",
      region: normalizeRegionCode(draft.region),
      phoneNumber: draft.phoneNumber || "",
      subdomain: draft.subdomain || "",
      organizationName: draft.organizationName || "",
    };
    setSignUpForm((prev) => ({
      ...prev,
      ...restoredForm,
    }));
    setSelectedPlan(draft.selectedPlan || "starter");
    if (draft.signupId) setSignupId(draft.signupId);
    if (draft.signupSessionToken) setSignupSessionToken(draft.signupSessionToken);
    if (draft.signupPaymentToken) setSignupPaymentToken(draft.signupPaymentToken);
    if (draft.locked) setSignupLocked(true);

    // Incomplete profile must not land on OTP — force details to re-fill.
    if (draft.step === "otp" && !isSignupProfileComplete(restoredForm)) {
      setSignUpStep("details");
      setDetailsConfirmedForOtp(false);
      setSignUpFeedback({
        type: "error",
        message: INCOMPLETE_SIGNUP_MESSAGE,
      });
      setShowSignUpModal(true);
      setShowSignInModal(false);
      return;
    }

    setSignUpStep(draft.step);
    if (draft.step === "otp") {
      setDetailsConfirmedForOtp(true);
      if (draft.otpLockedUntil && draft.otpLockedUntil > Date.now()) {
        setSignUpOtpLockedUntil(draft.otpLockedUntil);
      }
      if (typeof draft.resendAvailableAt === "number") {
        setSignUpResendAvailableAt(draft.resendAvailableAt);
      }
    }
    setShowSignUpModal(true);
    setShowSignInModal(false);
  }, [signupNext]);

  // Sync plan prices/limits from the app's live subscription plans instead of
  // hardcoding them — keeps this page from drifting from what's actually charged.
  const fetchPlansByName = async () => {
    const res = await fetchPlans();
    const plans = normalizePlansPayload(res.data);
    const byName = {};
    plans.forEach((p) => {
      byName[(p.plan_name || "").toLowerCase()] = p;
    });
    setPlansByName(byName);
    return byName;
  };

  useEffect(() => {
    fetchPlansByName().catch((error) => {
      // Keep the fallback hardcoded price/features if the API isn't reachable.
      console.error("[NovaLanding] Failed to load /plans lambda endpoint", error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setNavShadow(window.scrollY > 6);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer for reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const toggleNav = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const openContactModal = (event) => {
    event?.preventDefault();
    setMobileMenuOpen(false);
    setShowContactModal(true);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
  };

  /**
   * Reset in-memory signup funnel (+ optional draft / pendingSignup).
   * Intent: one abandon/create-fresh path so tokens and wait hints do not leak
   * across attempts (hard refresh → new signup, dismiss wait, go to login).
   */
  const resetSignupClientState = ({
    clearPending = false,
    clearForm = false,
    clearDraft = true,
  } = {}) => {
    setSignUpStep("details");
    setSignUpOtp("");
    setSignupOrder(null);
    setPaidSignupResponse(null);
    setSignupId("");
    setSignupSessionToken("");
    setSignupPaymentToken("");
    setSignupLocked(false);
    setDetailsConfirmedForOtp(false);
    setSubdomainAvailable(null);
    setSubdomainError("");
    setSignupFieldErrors({});
    setSignUpFeedback({ type: "", message: "" });
    setSignUpOtpLockedUntil(null);
    setSignUpResendAvailableAt(null);
    checkoutAutoStartedRef.current = false;
    if (clearDraft) clearSignupDraft();
    if (clearPending) clearPendingSignup();
    if (clearForm) {
      setSignUpForm({ ...EMPTY_SIGNUP_FORM });
      setHasGstin(false);
      setGstinError("");
    }
  };

  /**
   * Open the wait-provisioning modal with the correct title variant.
   * payment_success = just paid/activated; already_provisioning = resume/API/pending.
   */
  const openProvisioningWait = ({
    email = "",
    domain = "",
    variant = PROVISIONING_VARIANT.ALREADY_PROVISIONING,
    message = "",
  } = {}) => {
    setProvisioningEmail(String(email || "").trim().toLowerCase());
    setProvisioningDomain(domain || "");
    setProvisioningVariant(variant);
    setProvisioningMessage(message || "");
    setShowSignUpModal(false);
    setShowSignInModal(false);
    setShowProvisioningModal(true);
  };

  const openSignUpModal = (plan = "starter") => {
    // Explicit new signup: drop prior wait hint and continuity tokens.
    resetSignupClientState({ clearPending: true, clearDraft: true });
    setShowSignUpModal(true);
    setShowProvisioningModal(false);
    setSelectedPlan(plan);
    writeSignupDraft(signUpForm, plan, plansByName, { step: "details" });
  };

  /** Apply a signup snapshot (create / update / resume) onto form + persisted draft. */
  const applySignupSnapshot = (snapshot, { step, message, messageType = "success" } = {}) => {
    const pick = (snapVal, formVal) =>
      snapVal !== undefined && snapVal !== null ? snapVal : formVal;
    const nextForm = {
      ...signUpForm,
      firstName: pick(snapshot.firstName, signUpForm.firstName),
      lastName: pick(snapshot.lastName, signUpForm.lastName),
      email: String(pick(snapshot.email, signUpForm.email) || "")
        .trim()
        .toLowerCase(),
      phoneNumber: pick(snapshot.phoneNumber, signUpForm.phoneNumber),
      organizationName: pick(
        snapshot.tenantName,
        signUpForm.organizationName,
      ),
      subdomain: normalizeSubdomainInput(
        pick(
          snapshot.subdomain || snapshot.tenantSlug,
          signUpForm.subdomain,
        ) || "",
      ),
      region: normalizeRegionCode(
        pick(snapshot.region, signUpForm.region),
      ),
      gstin: String(pick(snapshot.gstin, signUpForm.gstin) || "").toUpperCase(),
    };
    setSignUpForm(nextForm);
    setHasGstin(Boolean(String(nextForm.gstin || "").trim()));
    if (snapshot.signupId) setSignupId(snapshot.signupId);
    if (snapshot.signupSessionToken) {
      setSignupSessionToken(snapshot.signupSessionToken);
    }
    if (snapshot.signupPaymentToken) {
      setSignupPaymentToken(snapshot.signupPaymentToken);
    }
    const locked = Boolean(snapshot.locked) || step === "payment";
    setSignupLocked(locked);
    const resendAvailableAt =
      step === "otp" ? signupResendAvailableFromSentAt(snapshot.otpSentAt) : null;
    if (step === "otp") {
      setDetailsConfirmedForOtp(true);
      setSignUpResendAvailableAt(resendAvailableAt);
      setSignUpOtpLockedUntil(null);
    }
    if (step === "payment") {
      setSignupSessionToken("");
      try {
        sessionStorage.removeItem("marketplace_session");
      } catch {
        /* ignore */
      }
      setMarketplaceSession("");
      setSignupOrder(null);
      setPaidSignupResponse(null);
    }
    if (step) setSignUpStep(step);
    writeSignupDraft(nextForm, selectedPlan, plansByName, {
      step: step || signUpStep,
      signupId: snapshot.signupId || signupId,
      signupSessionToken:
        step === "payment" ? "" : snapshot.signupSessionToken || signupSessionToken,
      signupPaymentToken: snapshot.signupPaymentToken || signupPaymentToken,
      tenantSlug: snapshot.tenantSlug || nextForm.subdomain,
      locked,
      ...(step === "otp"
        ? {
            resendAvailableAt,
            otpLockedUntil: 0,
          }
        : {}),
    });
    if (message) {
      setSignUpFeedback({ type: messageType, message });
    }
  };

  /** Locked payment screen after confirm / resume-payment / SIGNUP_LOCKED. */
  const goToLockedPayment = (snapshot, message) => {
    applySignupSnapshot(
      { ...snapshot, locked: true, signupSessionToken: "" },
      {
        step: "payment",
        message:
          message ||
          snapshot.message ||
          "Email confirmed. Continue to payment for your selected plan.",
      },
    );
  };

  /** Switch signup modal over to workspace login when account is already ACTIVE. */
  const goToWorkspaceLogin = (email, message) => {
    setShowSignUpModal(false);
    setShowProvisioningModal(false);
    setShowSignInModal(true);
    setSignInForm((prev) => ({
      ...prev,
      email: (email || "").trim().toLowerCase(),
      password: "",
    }));
    setSignInSuccess(
      message ||
        "This email already has a workspace. Log in instead of signing up again.",
    );
    setSignInError("");
    resetSignupClientState({ clearPending: true, clearDraft: true });
  };

  const showPricingAlert = (tier, type, message) => {
    setPricingAlert({ tier, type, message });
  };

  const dismissPricingAlert = () => {
    setPricingAlert({ tier: "", type: "", message: "" });
  };

  useEffect(() => {
    if (!pricingAlert.message) return undefined;
    const timer = setTimeout(dismissPricingAlert, 4000);
    return () => clearTimeout(timer);
  }, [pricingAlert]);

  const allowedDomain = useMemo(() => {
    const subdomain = normalizeSubdomainInput(signUpForm.subdomain);
    return subdomain ? `${subdomain}.${TENANT_ROOT_DOMAIN}` : "";
  }, [signUpForm.subdomain]);

  const handleSignUpChange = (e) => {
    const { name, value } = e.target;
    if (signupLocked && name !== "password" && name !== "confirmPassword") {
      return;
    }
    if (name === "email") {
      const nextEmail = value.trim().toLowerCase();
      const draft = readSignupDraft();
      const draftEmail = (draft?.email || "").trim().toLowerCase();
      const continuityEmail = (signUpForm.email || "").trim().toLowerCase();
      // Do not carry signupId / tokens from a previous email into a new identity.
      if (
        (signupId || draft?.signupId) &&
        ((draftEmail && draftEmail !== nextEmail) ||
          (continuityEmail && continuityEmail !== nextEmail))
      ) {
        resetSignupClientState({ clearDraft: true, clearPending: false });
      }
      setSignUpForm((prev) => ({ ...prev, email: value }));
    } else if (name === "subdomain") {
      const normalized = normalizeSubdomainInput(value);
      setSignUpForm((prev) => ({ ...prev, subdomain: normalized }));
      setSubdomainError(validateSubdomain(normalized));
      setSubdomainAvailable(null);
      setSignupFieldErrors((prev) => {
        if (!prev.subdomain) return prev;
        const next = { ...prev };
        delete next.subdomain;
        return next;
      });
    } else if (name === "region") {
      const regionCode = normalizeRegionCode(value);
      const nationalDigits = getSignupNationalDigits(
        signUpForm.phoneNumber,
        signUpForm.region,
      );
      const nextPhone = composeSignupPhoneNumber(nationalDigits, regionCode);
      setSignUpForm((prev) => ({
        ...prev,
        region: regionCode,
        phoneNumber: nextPhone,
      }));
      setSignupFieldErrors((prev) => {
        const hadRegionError = Boolean(prev.region);
        const hadPhoneError = Boolean(prev.phoneNumber);
        if (!hadRegionError && !hadPhoneError) return prev;
        const formForValidation = {
          ...signUpForm,
          region: regionCode,
          phoneNumber: nextPhone,
        };
        const next = { ...prev };
        if (hadRegionError) {
          const regionMsg = validateSignupField("region", formForValidation);
          if (regionMsg) next.region = regionMsg;
          else delete next.region;
        }
        if (hadPhoneError) {
          const phoneMsg = validateSignupField("phoneNumber", formForValidation);
          if (phoneMsg) next.phoneNumber = phoneMsg;
          else delete next.phoneNumber;
        }
        return next;
      });
    } else if (name === "phoneNumber") {
      setSignUpForm((prev) => ({
        ...prev,
        phoneNumber: composeSignupPhoneNumber(value, prev.region),
      }));
    } else {
      setSignUpForm((prev) => ({ ...prev, [name]: value }));
    }
    if (signUpFeedback.message) {
      setSignUpFeedback({ type: "", message: "" });
    }
    // Persist errors until the field is valid (do not clear on every keystroke).
    // Subdomain already live-validates above; region handled in its branch.
    if (name !== "subdomain" && name !== "region") {
      let formForValidation = { ...signUpForm, [name]: value };
      if (name === "email") {
        formForValidation = { ...signUpForm, email: value };
      } else if (name === "phoneNumber") {
        formForValidation = {
          ...signUpForm,
          phoneNumber: composeSignupPhoneNumber(value, signUpForm.region),
        };
      }
      setSignupFieldErrors((prev) => {
        const hadFieldError = Boolean(prev[name]);
        const hadConfirmError = name === "password" && Boolean(prev.confirmPassword);
        if (!hadFieldError && !hadConfirmError) return prev;

        const next = { ...prev };
        if (hadFieldError) {
          const message = validateSignupField(name, formForValidation);
          if (message) next[name] = message;
          else delete next[name];
        }
        if (hadConfirmError) {
          const confirmMsg = validateSignupField(
            "confirmPassword",
            formForValidation,
          );
          if (confirmMsg) next.confirmPassword = confirmMsg;
          else delete next.confirmPassword;
        }
        return next;
      });
    }
  };

  /**
   * Validates one signup field when the user leaves it, and shows the
   * under-field error + red outline without submitting.
   */
  const handleSignUpBlur = (e) => {
    const { name, value } = e.target;
    if (signupLocked && name !== "password" && name !== "confirmPassword") {
      return;
    }
    // Merge the blurred control's value so we are not racing React setState.
    let formForValidation = { ...signUpForm, [name]: value };
    if (name === "subdomain") {
      formForValidation = {
        ...signUpForm,
        subdomain: normalizeSubdomainInput(value),
      };
    } else if (name === "region") {
      formForValidation = {
        ...signUpForm,
        region: normalizeRegionCode(value),
      };
    } else if (name === "phoneNumber") {
      formForValidation = {
        ...signUpForm,
        phoneNumber: composeSignupPhoneNumber(value, signUpForm.region),
      };
    }
    const message = validateSignupField(name, formForValidation);
    // Subdomain: local format can pass while check-subdomain marked it taken.
    // Do not wipe the API error on blur when availability is still false.
    if (name === "subdomain") {
      if (message) {
        setSignupFieldErrors((prev) => ({ ...prev, subdomain: message }));
        setSubdomainError(message);
      } else if (subdomainAvailable === false) {
        const kept =
          signupFieldErrors.subdomain ||
          subdomainError ||
          "This workspace URL is already taken. Try a different URL prefix.";
        setSignupFieldErrors((prev) => ({
          ...prev,
          subdomain: prev.subdomain || kept,
        }));
        setSubdomainError(kept);
      } else {
        setSignupFieldErrors((prev) => {
          if (!prev.subdomain) return prev;
          const next = { ...prev };
          delete next.subdomain;
          return next;
        });
        setSubdomainError("");
      }
      return;
    }
    setSignupFieldErrors((prev) => {
      const next = { ...prev };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
  };

  const handleGstinCheckboxChange = (e) => {
    const checked = e.target.checked;
    setHasGstin(checked);
    if (!checked) {
      setGstinError("");
      setSignUpForm((prev) => ({ ...prev, gstin: "" }));
      return;
    }
    window.setTimeout(() => {
      document
        .querySelector('[data-testid="landing-sign-up-gstin-input"]')
        ?.focus();
    }, 0);
  };

  // Enter on a checkbox inside a form submits the form. Toggle instead so Tab
  // + Enter/Space can check "Add GSTIN" the same way a mouse click does.
  const handleGstinCheckboxKeyDown = (e) => {
    if (e.key !== "Enter" || signupLocked) return;
    e.preventDefault();
    handleGstinCheckboxChange({ target: { checked: !hasGstin } });
  };

  const handleGstinChange = (e) => {
    const value = e.target.value.toUpperCase();
    setSignUpForm((prev) => ({ ...prev, gstin: value }));
    if (gstinError) setGstinError("");
  };

  const handleGstinBlur = () => {
    if (!hasGstin) {
      setGstinError("");
      return;
    }
    setGstinError(getGstinError(signUpForm.gstin));
  };

  const handleSignInChange = (e) => {
    const { name, value } = e.target;
    setSignInForm((prev) => ({ ...prev, [name]: value }));
    if (signInError) {
      setSignInError("");
    }
    if (signInSuccess) {
      setSignInSuccess("");
    }
  };

  const handlePasswordResetChange = (e) => {
    const { name, value } = e.target;
    setPasswordResetForm((prev) => ({ ...prev, [name]: value }));
    if (signInError) {
      setSignInError("");
    }
    if (signInSuccess) {
      setSignInSuccess("");
    }
  };

  const openForgotPassword = () => {
    setPasswordResetForm((prev) => ({
      ...prev,
      email: signInForm.email.trim().toLowerCase(),
    }));
    setPasswordResetStep("email");
    setShowSignInModal(false);
    setShowPasswordResetModal(true);
    setSignInError("");
    setSignInSuccess("");
  };

  const backToSignIn = () => {
    setPasswordResetStep("signin");
    setShowPasswordResetModal(false);
    setShowSignInModal(true);
    setSignInError("");
    setSignInSuccess("");
  };

  const isTenantAlreadyExistsError = (message = "") =>
    /tenant.*already exists/i.test(message);

  const isExistingSignupError = (message = "") => {
    if (isTenantAlreadyExistsError(message)) {
      return false;
    }
    return /usernameexistsexception|user.*exist|email.*already|already.*registered|already.*exist/i.test(
      message,
    );
  };

  const isAlreadyConfirmedError = (message = "") =>
    /already.*confirm|already.*verified|confirmed.*already/i.test(message);

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    const email = passwordResetForm.email.trim().toLowerCase();

    if (!email) {
      setSignInError("Email is required.");
      return;
    }

    const forgotEmailError = getEmailError(email);
    if (forgotEmailError) {
      setSignInError(forgotEmailError);
      return;
    }

    try {
      setPasswordResetLoading(true);
      setSignInError("");
      setSignInSuccess("");
      await forgotPassword({ email });
      setPasswordResetForm((prev) => ({ ...prev, email }));
      setPasswordResetStep("reset");
      setResetOtpExpired(false);
      setResetOtpExpiresAt(startOtpTimer("reset", email));
      setSignInSuccess("We sent a verification code to your email.");
    } catch (error) {
      const code = getApiErrorCode(error);
      if (isOtpExpiredCode(code)) {
        setResetOtpExpired(true);
      }
      setSignInError(
        getOtpUserMessage(
          error,
          getApiErrorMessage(error, "Unable to send password reset code."),
        ),
      );
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (resetPasswordSubmittingRef.current) {
      return;
    }
    const email = passwordResetForm.email.trim().toLowerCase();
    const otp = passwordResetForm.otp.trim();
    const newPassword = passwordResetForm.newPassword;

    if (!email || !otp || !newPassword || !passwordResetForm.confirmPassword) {
      setSignInError("Email, OTP, and new password are required.");
      return;
    }

    if (otp.length !== 6) {
      setSignInError("Please enter a valid 6-digit code.");
      return;
    }

    const resetEmailFormatError = getEmailError(email);
    if (resetEmailFormatError) {
      setSignInError(resetEmailFormatError);
      return;
    }

    if (newPassword !== passwordResetForm.confirmPassword) {
      setSignInError("Passwords do not match.");
      return;
    }

    const passwordError = getPasswordError(newPassword, "New password");
    if (passwordError) {
      setSignInError(passwordError);
      return;
    }

    if (resetOtpExpired) {
      setSignInError("This code has expired. Click Resend OTP to get a new one.");
      return;
    }

    try {
      resetPasswordSubmittingRef.current = true;
      setPasswordResetLoading(true);
      setSignInError("");
      setSignInSuccess("");
      await resetPassword({ email, confirmationCode: otp, newPassword });
      setSignInForm({ email, password: "" });
      setPasswordResetForm({
        email: "",
        otp: "",
        newPassword: "",
        confirmPassword: "",
      });
      clearOtpTimer("reset");
      setPasswordResetStep("success");
      setSignInError("");
      setSignInSuccess("");
    } catch (error) {
      const code = getApiErrorCode(error);
      if (isOtpExpiredCode(code)) {
        setResetOtpExpired(true);
      }
      setSignInError(
        getOtpUserMessage(
          error,
          getApiErrorMessage(error, "Unable to reset password."),
        ),
      );
    } finally {
      resetPasswordSubmittingRef.current = false;
      setPasswordResetLoading(false);
    }
  };

  const handleExternalLogin = async (e) => {
    e.preventDefault();

    if (!signInForm.email.trim() || !signInForm.password.trim()) {
      setSignInError("Email and password are required.");
      return;
    }

    const signInEmailFormatError = getEmailError(signInForm.email);
    if (signInEmailFormatError) {
      setSignInError(signInEmailFormatError);
      return;
    }

    const passwordError = getLoginPasswordError(signInForm.password);
    if (passwordError) {
      setSignInError(passwordError);
      return;
    }

    try {
      setSignInLoading(true);
      setSignInError("");
      setSignInSuccess("");
      const response = await loginUser({
        email: signInForm.email.trim().toLowerCase(),
        password: signInForm.password,
      });
      setShowSignInModal(false);
      setSignInForm({ email: "", password: "" });
      clearPendingSignup();
      clearSignupDraft();
      // window.location.href = "/home";
      const { idToken, tenantHost } = response.data;
      logToServer("INFO", "Login response received", {
        hasIdToken: Boolean(idToken),
        tenantHost,
      });
      // Temporary storage only for bootstrap
      sessionStorage.setItem("cognito_id_token", idToken);

      if (!tenantHost) {
        logToServer("ERROR", "Tenant host missing in login response");
        window.location.href = "/";
        return;
      }

      let employeeAppUrl = tenantHost;
      // let employeeAppUrl = "http://localhost:3000";
      if (
        !employeeAppUrl.startsWith("http://") &&
        !employeeAppUrl.startsWith("https://")
      ) {
        employeeAppUrl = `https://${employeeAppUrl}`;
      }

      window.location.href = `${employeeAppUrl}/bootstrap#id_token=${encodeURIComponent(idToken)}`;
      return;
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        "Unable to sign in with email and password.",
      );
      setSignInError(message);
      logToServer("ERROR", `Error after signin: ${message}`);
    } finally {
      setSignInLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      setGoogleSignInLoading(true);
      setSignInError("");
      setSignInSuccess("");
      loginWithGoogle();
    } catch (error) {
      console.error("Google login start failed:", error);
      setSignInError(
        getApiErrorMessage(error, "Unable to start Google sign-in."),
      );
      setGoogleSignInLoading(false);
    }
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    if ((signUpOtpLockedUntil || 0) > Date.now()) {
      setSignUpFeedback({
        type: "error",
        message: getOtpUserMessage({ code: SIGNUP_ERROR_CODES.OTP_LIMIT }),
      });
      return;
    }
    if (signupLocked) {
      setSignUpStep("payment");
      setSignUpFeedback({
        type: "success",
        message: "Your email is already verified. Continue to payment below.",
      });
      return;
    }

      const normalizedPhone = composeSignupPhoneNumber(
        getSignupNationalDigits(signUpForm.phoneNumber, signUpForm.region),
        signUpForm.region,
      );
      if (normalizedPhone !== signUpForm.phoneNumber) {
        setSignUpForm((prev) => ({ ...prev, phoneNumber: normalizedPhone }));
      }
      const detailsErrors = validateSignupForm({
        ...signUpForm,
        phoneNumber: normalizedPhone,
      });
      setSignupFieldErrors(detailsErrors);
      if (detailsErrors.subdomain) {
        setSubdomainError(detailsErrors.subdomain);
      }
      if (Object.keys(detailsErrors).length > 0) {
        return;
      }

      if (hasGstin) {
        const gstinFormatError = getGstinError(signUpForm.gstin);
        if (gstinFormatError) {
          setGstinError(gstinFormatError);
          return;
        }
      }

    if (subdomainAvailable === false) {
      const reservedByOwnDraft =
        /temporarily reserved|another signup/i.test(subdomainError || "");
      // Stale soft-hold copy (own unpaid SignupSession) must not block submit;
      // POST /signup is the source of truth (TenantRegistry + own-session resume).
      if (!reservedByOwnDraft) {
        const takenMsg =
          subdomainError ||
          "This workspace URL is already taken. Try a different URL prefix.";
        setSignupFieldErrors((prev) => ({ ...prev, subdomain: takenMsg }));
        setSubdomainError(takenMsg);
        return;
      }
    }

    const firstName = signUpForm.firstName.trim();
    const lastName = signUpForm.lastName.trim();
    const phone = signUpForm.phoneNumber.trim();
    const activeMarketplaceSession =
      getActiveMarketplaceSession(marketplaceSession);
    // Update keeps the same Cognito user. A fresh signup would delete it and send another code.
    const isUpdate = Boolean(signupId && signupSessionToken);

      try {
        setSignUpLoading(true);
        setSignUpFeedback({ type: "", message: "" });
        const result = await registerUser({
          firstName,
          lastName,
          email: signUpForm.email.trim().toLowerCase(),
          phoneNumber: normalizedPhone,
          tenantName: signUpForm.organizationName.trim(),
          subdomain: normalizeSubdomainInput(signUpForm.subdomain),
          region: signUpForm.region,
          password: signUpForm.password,
          signupId: isUpdate ? signupId : undefined,
          signupSessionToken: isUpdate ? signupSessionToken : undefined,
          gstin: hasGstin ? signUpForm.gstin.trim().toUpperCase() : "",
        });

      if (result?.success !== true) {
        throw new AuthServiceError(
          result?.message || "Unable to start signup.",
        );
      }

      const next =
        result.nextStep === "payment" || result.locked ? "payment" : "otp";
      if (next === "payment") {
        goToLockedPayment(result, result.message);
      } else {
        setSignUpOtp("");
        setDetailsConfirmedForOtp(true);
        applySignupSnapshot(result, {
          step: "otp",
          message:
            result.message ||
            "Verification code sent. Enter the OTP below to verify your email.",
        });
      }
    } catch (error) {
      await handleSignupApiError(error, {
        fallback: "Unable to start signup.",
        context: "submit",
      });
    } finally {
      setSignUpLoading(false);
    }
  };

  /**
   * TenantRegistry claimed the subdomain at payment (or on submit).
   * Unlock the form, drop continuity tokens so the next submit is a fresh
   * create/restart, and focus the user on choosing a new workspace URL.
   */
  const goBackToSignupToChangeSubdomain = (message) => {
    const msg =
      message ||
      "This workspace URL is already taken. Choose a different URL prefix and submit again.";
    setShowSignUpModal(true);
    setShowProvisioningModal(false);
    setSignupLocked(false);
    setSignupId("");
    setSignupSessionToken("");
    setSignupPaymentToken("");
    setSignupOrder(null);
    setPaidSignupResponse(null);
    setDetailsConfirmedForOtp(false);
    setSignUpOtp("");
    checkoutAutoStartedRef.current = false;
    setSignUpStep("details");
    setSubdomainAvailable(false);
    setSubdomainError(msg);
    setSignupFieldErrors((prev) => ({ ...prev, subdomain: msg }));
    setSignUpFeedback({ type: "error", message: msg });
    writeSignupDraft(signUpForm, selectedPlan, plansByName, {
      step: "details",
      signupId: "",
      signupSessionToken: "",
      signupPaymentToken: "",
      tenantSlug: normalizeSubdomainInput(signUpForm.subdomain),
      locked: false,
    });
  };

  /**
   * Route signup API failures using errors[0].code / next_step (signup_flow.md).
   * Shows backend message verbatim.
   */
  const handleSignupApiError = async (error, { fallback, context } = {}) => {
    const code =
      error instanceof AuthServiceError
        ? error.code
        : getApiErrorCode(error);
    const extras =
      error instanceof AuthServiceError
        ? error.extras || {}
        : getApiErrorExtras(error);
    const nextStep = extras.next_step || "";
    const message =
      (error instanceof AuthServiceError && error.message?.trim()) ||
      getApiErrorMessage(error, fallback || "Something went wrong.");
    const email = (extras.email || signUpForm.email || "").trim().toLowerCase();

    applySignupFieldErrors(error);

    // One Marketplace subscription → one workspace (before generic payment/login paths).
    if (code === SIGNUP_ERROR_CODES.MARKETPLACE_ALREADY_LINKED) {
      const catalog = MARKETPLACE_ERROR_MESSAGES[code] || message;
      if (nextStep === "login") {
        try {
          sessionStorage.removeItem("marketplace_session");
        } catch {
          /* ignore */
        }
        setMarketplaceSession("");
        goToWorkspaceLogin(email, catalog);
        return;
      }
      if (nextStep === "wait_provisioning") {
        openProvisioningWait({
          email: email || signUpForm.email.trim().toLowerCase(),
          domain: resolveBoundTenantDomain(extras),
          variant: PROVISIONING_VARIANT.ALREADY_PROVISIONING,
          message: catalog,
        });
        return;
      }
      if (nextStep === "payment") {
        // Do not resume-payment as the wrong email; send to resume-payment screen
        // so the original buyer can continue with their credentials.
        try {
          sessionStorage.removeItem("marketplace_session");
        } catch {
          /* ignore */
        }
        setMarketplaceSession("");
        setSignUpStep("resume-payment");
        setSignUpFeedback({ type: "error", message: catalog });
        return;
      }
      try {
        sessionStorage.removeItem("marketplace_session");
      } catch {
        /* ignore */
      }
      setMarketplaceSession("");
      setSignUpStep("details");
      setSignUpFeedback({ type: "error", message: catalog });
      return;
    }

    if (code === SIGNUP_ERROR_CODES.ACCOUNT_READY || nextStep === "login") {
      try {
        sessionStorage.removeItem("marketplace_session");
      } catch {
        /* ignore */
      }
      setMarketplaceSession("");
      goToWorkspaceLogin(email, message);
      return;
    }

    if (code === SIGNUP_ERROR_CODES.SIGNUP_PROVISIONING) {
      openProvisioningWait({
        email: email || signUpForm.email.trim().toLowerCase(),
        // Bound-tenant host/slug from Lambda — not the form's typed URL prefix.
        domain: resolveBoundTenantDomain(extras),
        variant: PROVISIONING_VARIANT.ALREADY_PROVISIONING,
        message,
      });
      return;
    }

    if (
      code === SIGNUP_ERROR_CODES.EMAIL_ALREADY_CONFIRMED ||
      ((code === SIGNUP_ERROR_CODES.SIGNUP_LOCKED || nextStep === "payment") &&
        context === "confirm")
    ) {
      goToLockedPayment(
        {
          email,
          firstName: extras.firstname,
          lastName: extras.lastname,
          tenantName: extras.tenant_name,
          tenantSlug: extras.tenant_slug || extras.subdomain,
          subdomain: extras.subdomain || extras.tenant_slug,
          region: extras.region,
          phoneNumber: extras.phonenumber,
          gstin: extras.gstin,
          signupId: extras.signup_id || signupId,
          signupPaymentToken: extras.signup_payment_token || "",
          locked: true,
        },
        message,
      );
      return;
    }

    if (code === SIGNUP_ERROR_CODES.SIGNUP_LOCKED || nextStep === "payment") {
      if (signUpForm.password || resumePaymentForm.password) {
        try {
          const snap = await resumeSignupPayment({
            email: email || signUpForm.email.trim().toLowerCase() || resumePaymentForm.email,
            password: signUpForm.password || resumePaymentForm.password,
          });
          goToLockedPayment(snap, message || snap.message);
          return;
        } catch (resumeError) {
          setSignUpFeedback({
            type: "error",
            message: getApiErrorMessage(resumeError, message),
          });
          setSignUpStep("resume-payment");
          return;
        }
      }
      setSignUpStep("resume-payment");
      setResumePaymentForm((prev) => ({
        ...prev,
        email: email || prev.email || signUpForm.email,
      }));
      setSignUpFeedback({ type: "error", message });
      return;
    }

    if (
      code === SIGNUP_ERROR_CODES.PASSWORD_INVALID ||
      code === SIGNUP_ERROR_CODES.VALIDATION_ERROR
    ) {
      setSignUpStep("details");
      setSignUpFeedback({ type: "error", message });
      return;
    }

    if (code === SIGNUP_ERROR_CODES.PROVISION_START_FAILED) {
      setSignUpFeedback({
        type: "error",
        message:
          message ||
          "Payment may have succeeded but workspace setup failed. Contact support — you will not be charged again.",
      });
      return;
    }

    if (code === SIGNUP_ERROR_CODES.SIGNUP_BACKEND_MISCONFIGURED) {
      setSignUpFeedback({
        type: "error",
        message:
          message ||
          "Signup is temporarily unavailable. Please try again in a few minutes.",
      });
      return;
    }

    if (
      code === SIGNUP_ERROR_CODES.SIGNUP_SESSION_EXPIRED ||
      code === SIGNUP_ERROR_CODES.SIGNUP_SESSION_INVALID
    ) {
      if (signUpForm.password && (email || signUpForm.email)) {
        try {
          const snap = await resumeSignupDraft({
            email: email || signUpForm.email.trim().toLowerCase(),
            password: signUpForm.password,
          });
          if (snap.nextStep === "payment" || snap.locked) {
            goToLockedPayment(snap, snap.message || message);
          } else {
            applySignupSnapshot(snap, {
              step: "otp",
              message: snap.message || message,
            });
          }
          return;
        } catch (resumeError) {
          setSignUpFeedback({
            type: "error",
            message: getApiErrorMessage(resumeError, message),
          });
          return;
        }
      }
      setSignUpFeedback({ type: "error", message });
      setSignUpStep("details");
      return;
    }

    if (code === SIGNUP_ERROR_CODES.SIGNUP_AUTH_FAILED) {
      setSignUpFeedback({ type: "error", message });
      return;
    }

    if (
      code === SIGNUP_ERROR_CODES.SUBDOMAIN_TAKEN ||
      code === SIGNUP_ERROR_CODES.SUBDOMAIN_INVALID
    ) {
      // Payment-time TenantRegistry conflict (or live check): unlock and send
      // back to the form so the user can pick a new subdomain and submit again
      // as a fresh create (restart), not a locked update.
      goBackToSignupToChangeSubdomain(message);
      return;
    }

    if (code === SIGNUP_ERROR_CODES.EMAIL_TAKEN) {
      // Email belongs to another workspace — stay on the form (not wait popup).
      // ACTIVE + next_step=login is handled above via nextStep === "login".
      setSignUpStep("details");
      setShowSignUpModal(true);
      setShowProvisioningModal(false);
      setSignupFieldErrors((prev) => ({
        ...prev,
        email:
          message ||
          "This email is already registered. Use a different email.",
      }));
      setSignUpFeedback({ type: "", message: "" });
      return;
    }

    if (code === SIGNUP_ERROR_CODES.SIGNUP_NOT_FOUND) {
      resetSignupClientState({ clearDraft: true, clearPending: false });
      setSignUpFeedback({ type: "error", message });
      return;
    }

    if (
      code === SIGNUP_ERROR_CODES.SIGNUP_PAYMENT_TOKEN_EXPIRED ||
      String(code).startsWith("SIGNUP_PAYMENT_TOKEN")
    ) {
      setSignUpStep("resume-payment");
      setResumePaymentForm((prev) => ({
        ...prev,
        email: email || prev.email || signUpForm.email,
      }));
      if (signUpForm.password || resumePaymentForm.password) {
        try {
          const snap = await resumeSignupPayment({
            email: email || signUpForm.email.trim().toLowerCase() || resumePaymentForm.email,
            password: signUpForm.password || resumePaymentForm.password,
          });
          goToLockedPayment(snap, message || snap.message);
          return;
        } catch (resumeError) {
          setSignUpFeedback({
            type: "error",
            message: getApiErrorMessage(resumeError, message),
          });
          return;
        }
      }
      setSignUpFeedback({
        type: "error",
        message:
          message ||
          "Your payment session expired. Enter your signup password to continue.",
      });
      return;
    }

    if (nextStep === "otp" || String(code).startsWith("OTP_") || isCognitoUnavailable(code)) {
      setSignUpStep("otp");
      if (isOtpExpiredCode(code)) {
        // The server already rejected the code. Drop the local pause so Resend
        // is clickable even if the countdown still showed a second left.
        setSignUpResendAvailableAt(0);
        setSignupOtpClock(Date.now());
        writeSignupDraft(signUpForm, selectedPlan, plansByName, {
          step: "otp",
          signupId,
          signupSessionToken,
          signupPaymentToken,
          resendAvailableAt: 0,
          otpLockedUntil: 0,
        });
      }
      if (isOtpLimitCode(code)) {
        const otpLockedUntil = Date.now() + SIGNUP_OTP_LOCKOUT_MS;
        setSignUpOtpLockedUntil(otpLockedUntil);
        writeSignupDraft(signUpForm, selectedPlan, plansByName, {
          step: "otp",
          signupId,
          signupSessionToken,
          signupPaymentToken,
          otpLockedUntil,
        });
      }
      setSignUpFeedback({
        type: "error",
        message: getOtpUserMessage(
          { code, message },
          message || "Something went wrong.",
        ),
      });
      return;
    }

    if (nextStep === "marketplace" || String(code).startsWith("MARKETPLACE_")) {
      try {
        sessionStorage.removeItem("marketplace_session");
      } catch {
        /* ignore */
      }
      setMarketplaceSession("");
      const catalog = MARKETPLACE_ERROR_MESSAGES[code] || message;
      setSignUpFeedback({ type: "error", message: catalog });
      return;
    }

    if (nextStep === "form") {
      setSignUpStep("details");
      setSignUpFeedback({ type: "error", message });
      return;
    }

    if (context === "submit" && isExistingSignupError(message)) {
      if (signUpForm.password) {
        try {
          const snap = await resumeSignupDraft({
            email: signUpForm.email.trim().toLowerCase(),
            password: signUpForm.password,
          });
          if (snap.nextStep === "payment" || snap.locked) {
            goToLockedPayment(snap, snap.message);
          } else {
            applySignupSnapshot(snap, {
              step: "otp",
              message: snap.message || message,
            });
          }
          return;
        } catch {
          /* fall through */
        }
      }
    }

    setSignUpFeedback({ type: "error", message });
  };

  /** After payment success: show wait-to-signin modal (no auto-login). */
  const completeSignupUI = (confirmedEmail) => {
    clearSignupDraft();
    checkoutAutoStartedRef.current = false;
    try {
      localStorage.setItem(
        PENDING_SIGNUP_KEY,
        JSON.stringify({
          firstName: signUpForm.firstName,
          lastName: signUpForm.lastName,
          email: confirmedEmail,
          region: signUpForm.region,
          phoneNumber: signUpForm.phoneNumber,
          subdomain: signUpForm.subdomain,
          organizationName: signUpForm.organizationName,
          type: "signup",
          confirmedAt: new Date().toISOString(),
        }),
      );
    } catch {
      // Modal still works for this visit even if persistence is unavailable.
    }
    setSignupOrder(null);
    setPaidSignupResponse(null);
    setSignupPaymentToken("");
    setDetailsConfirmedForOtp(false);
    setSignUpFeedback({ type: "", message: "" });
    openProvisioningWait({
      email: confirmedEmail,
      domain: allowedDomain,
      variant: PROVISIONING_VARIANT.PAYMENT_SUCCESS,
      message: "",
    });
  };

  // OTP confirms Cognito; response locks details and returns signup_payment_token.
  const finishSignupConfirmation = async () => {
    const confirmedEmail = signUpForm.email.trim().toLowerCase();
    if (marketplaceSession && !isMarketplaceSessionUnexpired(marketplaceSession)) {
      setSignUpFeedback({
        type: "error",
        message: MARKETPLACE_ERROR_MESSAGES.MARKETPLACE_SESSION_EXPIRED,
      });
      return;
    }
    const activeMarketplaceSession =
      getActiveMarketplaceSession(marketplaceSession);
    try {
      const resp = await confirmEmail({
        email: confirmedEmail,
        confirmationCode: signUpOtp.trim(),
        signupId: signupId || undefined,
        marketplaceSession: activeMarketplaceSession || undefined,
      });

      if (resp?.success === true) {
        goToLockedPayment(
          resp,
          resp.message ||
            "Email confirmed successfully. Continue to choose a plan and pay.",
        );
      } else {
        const msg = (resp && resp.message) || "Email verification failed.";
        setSignUpFeedback({ type: "error", message: msg });
      }
    } catch (error) {
      await handleSignupApiError(error, {
        fallback: "Email verification failed.",
        context: "confirm",
      });
    }
  };

  /** Submit the signup code. A wrong code stays retryable; only Cognito's attempt lock pauses the buttons. */
  const handleVerifySignUpOtp = async (e) => {
    e.preventDefault();

    if (signUpOtpVerifyingRef.current) {
      return;
    }

    if (!detailsConfirmedForOtp || !isSignupProfileComplete(signUpForm)) {
      setSignUpStep("details");
      setDetailsConfirmedForOtp(false);
      setSignUpFeedback({
        type: "error",
        message: INCOMPLETE_SIGNUP_MESSAGE,
      });
      return;
    }

    if (!signUpOtp.trim()) {
      setSignUpFeedback({
        type: "error",
        message: "Enter the code sent to your email.",
      });
      return;
    }
    if (signUpOtp.trim().length !== 6) {
      setSignUpFeedback({
        type: "error",
        message: "Enter a valid 6-digit code",
      });
      return;
    }

    if (signupOtpLocked) {
      setSignUpFeedback({
        type: "error",
        message: getOtpUserMessage({ code: SIGNUP_ERROR_CODES.OTP_LIMIT }),
      });
      return;
    }

    try {
      signUpOtpVerifyingRef.current = true;
      setSignUpLoading(true);
      await finishSignupConfirmation();
    } finally {
      signUpOtpVerifyingRef.current = false;
      setSignUpLoading(false);
    }
  };

  /** Return from OTP to the form so the user can fix email / details before confirm. */
  const handleEditSignupDetails = () => {
    if (signupLocked) {
      setSignUpFeedback({
        type: "error",
        message:
          "Your email is already verified. Details can no longer be edited.",
      });
      setSignUpStep("payment");
      return;
    }
    // Re-normalize region so the <select> matches REGION_OPTIONS after API lowercasing.
    const nextForm = {
      ...signUpForm,
      region: normalizeRegionCode(signUpForm.region),
    };
    setSignUpForm(nextForm);
    setSignUpStep("details");
    setSignUpOtp("");
    setSignupOrder(null);
    setPaidSignupResponse(null);
    setDetailsConfirmedForOtp(false);
    setSignUpFeedback({
      type: "success",
      message:
        "Update your details and submit again. Your signup will be updated in place.",
    });
    writeSignupDraft(nextForm, selectedPlan, plansByName, {
      step: "details",
      signupId,
      signupSessionToken,
      signupPaymentToken: "",
      tenantSlug: normalizeSubdomainInput(nextForm.subdomain),
      locked: false,
    });
  };

  /**
   * Resume-payment screen: email + password → POST /signup/resume-payment
   * (URL next=payment&marketplace=1 or expired payment token).
   */
  const handleResumePaymentSubmit = async (e) => {
    e.preventDefault();
    const email = resumePaymentForm.email.trim().toLowerCase();
    const password = resumePaymentForm.password;
    if (!email || !password) {
      setSignUpFeedback({
        type: "error",
        message: "Email and password are required to continue to payment.",
      });
      return;
    }
    try {
      setSignUpLoading(true);
      setSignUpFeedback({ type: "", message: "" });
      const snap = await resumeSignupPayment({ email, password });
      setSignUpForm((prev) => ({
        ...prev,
        email: snap.email || email,
        password,
        confirmPassword: password,
        firstName: snap.firstName || prev.firstName,
        lastName: snap.lastName || prev.lastName,
        organizationName: snap.tenantName || prev.organizationName,
        subdomain: normalizeSubdomainInput(
          snap.subdomain || snap.tenantSlug || prev.subdomain,
        ),
        region: normalizeRegionCode(snap.region || prev.region),
        phoneNumber: snap.phoneNumber || prev.phoneNumber,
      }));
      goToLockedPayment(
        snap,
        snap.message || "Continue to choose a plan and pay.",
      );
    } catch (error) {
      await handleSignupApiError(error, {
        fallback: "Unable to resume payment.",
        context: "resume-payment",
      });
    } finally {
      setSignUpLoading(false);
    }
  };

  const resolveSignupPlanIds = () => {
    const draft = readSignupDraft();
    const planKey = selectedPlan || draft?.selectedPlan || "starter";
    const plan = plansByName?.[planKey];
    const price = plan?.prices?.[0];
    const planId = plan?.id ?? draft?.plan_id;
    const priceId = price?.id ?? draft?.price_id;
    return { planKey, plan, price, planId, priceId };
  };

  // Pre-auth checkout — requires signup_id + signup_payment_token.
  const handleContinueToPayment = async () => {
    setSignUpLoading(true);
    setSignUpFeedback({ type: "", message: "" });
    const email = signUpForm.email.trim().toLowerCase();
    const tenantSlug = normalizeSubdomainInput(signUpForm.subdomain);
    const { planId, priceId } = resolveSignupPlanIds();

    if (!planId || !priceId) {
      setSignUpFeedback({
        type: "error",
        message:
          "Pricing isn't available right now. Please refresh and try again.",
      });
      setSignUpLoading(false);
      return;
    }

    try {
      let paymentToken = signupPaymentToken;
      let currentSignupId = signupId;

      if ((!paymentToken || !currentSignupId) && signUpForm.password) {
        const snap = await resumeSignupPayment({
          email,
          password: signUpForm.password,
        });
        paymentToken = snap.signupPaymentToken || paymentToken;
        currentSignupId = snap.signupId || currentSignupId;
        goToLockedPayment(snap, snap.message);
      }

      if (!paymentToken || !currentSignupId) {
        setSignUpFeedback({
          type: "error",
          message:
            "Your payment session expired. Enter the password you used when you signed up, then try again.",
        });
        setSignUpStep("resume-payment");
        setResumePaymentForm((prev) => ({
          ...prev,
          email: email || prev.email,
        }));
        setSignUpLoading(false);
        return;
      }

      const res = await postBilling(
        "/billing/signup/checkout",
        {
          email,
          tenant_slug: tenantSlug,
          plan_id: planId,
          price_id: priceId,
          signup_id: currentSignupId,
          signup_payment_token: paymentToken,
          gstin: hasGstin ? signUpForm.gstin.trim().toUpperCase() : undefined,
        },
        { timeout: 15000 },
      );
      setSignupOrder(res.data);
      writeSignupDraft(signUpForm, selectedPlan, plansByName, {
        step: "payment",
        plan_id: planId,
        price_id: priceId,
        signupId: currentSignupId,
        signupPaymentToken: paymentToken,
        tenantSlug,
        locked: true,
      });
    } catch (error) {
      const code =
        error instanceof AuthServiceError
          ? error.code
          : error?.response?.data?.errors?.[0]?.code;
      if (
        code === "MARKETPLACE_SESSION_EXPIRED" ||
        code === "MARKETPLACE_SESSION_INVALID"
      ) {
        try {
          sessionStorage.removeItem("marketplace_session");
        } catch {
          // ignore
        }
        setMarketplaceSession("");
      }
      // SUBDOMAIN_TAKEN at checkout → unlock form; other codes via shared router.
      await handleSignupApiError(error, {
        fallback: "We couldn't start checkout. Please try again.",
        context: "checkout",
      });
    } finally {
      setSignUpLoading(false);
    }
  };

  /**
   * After email verify, skip the Confirm screen and create the checkout order
   * automatically so the user lands on Pay / Activate.
   */
  useEffect(() => {
    if (signUpStep !== "payment") {
      checkoutAutoStartedRef.current = false;
      return undefined;
    }
    if (signupOrder || checkoutAutoStartedRef.current) {
      return undefined;
    }
    checkoutAutoStartedRef.current = true;
    handleContinueToPayment();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per payment entry without order
  }, [signUpStep, signupOrder]);

  const handleActivateFreeSignup = async () => {
    if (!signupOrder?.signup_token || !signupOrder?.order_id) return;
    setSignUpLoading(true);
    setSignUpFeedback({ type: "", message: "" });
    const email = signUpForm.email.trim().toLowerCase();
    const tenantSlug = normalizeSubdomainInput(signUpForm.subdomain);
    try {
      await postBilling(
        "/billing/signup/activate-free",
        {
          signup_token: signupOrder.signup_token,
          signup_id: signupId || undefined,
          email,
          tenant_slug: tenantSlug,
          plan_id: signupOrder.plan_id,
          price_id: signupOrder.price_id,
          order_id: signupOrder.order_id,
        },
        { timeout: 20000 },
      );
      completeSignupUI(email);
    } catch (error) {
      await handleSignupApiError(error, {
        fallback: "Couldn't activate your free plan. Please try again.",
        context: "activate-free",
      });
    } finally {
      setSignUpLoading(false);
    }
  };

  // Open Razorpay for paid signup orders (no login).
  const handlePayForSignup = async () => {
    if (!signupOrder) return;
    if (signupOrder.is_free || signupOrder.amount === 0) {
      await handleActivateFreeSignup();
      return;
    }
    setSignUpLoading(true);
    setSignUpFeedback({ type: "", message: "" });
    try {
      await openRazorpayCheckout({
        key: signupOrder.key_id,
        order_id: signupOrder.order_id,
        amount: signupOrder.amount,
        currency: signupOrder.currency,
        name: signupOrder.name,
        description: signupOrder.description,
        image: signupOrder.image || undefined,
        theme: { color: signupOrder.theme_color || "#3399cc" },
        prefill: { email: signUpForm.email.trim().toLowerCase() },
        handler: (response) => {
          onSignupPaymentSuccess(response);
        },
        modal: {
          ondismiss: () => {
            setPaidSignupResponse((current) => {
              if (!current) {
                setSignUpLoading(false);
                setSignUpFeedback({
                  type: "error",
                  message: "Payment was not completed. You can try again below.",
                });
              }
              return current;
            });
          },
        },
        onFailure: (response) => {
          setSignUpLoading(false);
          setSignUpFeedback({
            type: "error",
            message:
              response?.error?.description ||
              "Payment failed. You can try again with the same or a different card.",
          });
        },
      });
    } catch (error) {
      setSignUpLoading(false);
      setSignUpFeedback({
        type: "error",
        message: "Couldn't open Razorpay checkout. Please try again.",
      });
    }
  };

  const onSignupPaymentSuccess = async (response) => {
    // Payment captured on Razorpay — retry verify without charging again.
    setPaidSignupResponse(response);
    setSignUpFeedback({ type: "", message: "" });
    const email = signUpForm.email.trim().toLowerCase();
    const tenantSlug = normalizeSubdomainInput(signUpForm.subdomain);
    try {
      await postBilling(
        "/billing/signup/verify",
        {
          signup_token: signupOrder.signup_token,
          signup_id: signupId || undefined,
          email,
          tenant_slug: tenantSlug,
          plan_id: signupOrder.plan_id,
          price_id: signupOrder.price_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature || "",
        },
        { timeout: 20000 },
      );
      completeSignupUI(email);
    } catch (error) {
      await handleSignupApiError(error, {
        fallback:
          "Payment was received but we couldn't start workspace setup. Click below to retry — you won't be charged again.",
        context: "verify",
      });
    } finally {
      setSignUpLoading(false);
    }
  };

  const closeProvisioningModal = () => {
    setShowProvisioningModal(false);
    setProvisioningEmail("");
    setProvisioningDomain("");
    setProvisioningMessage("");
    setProvisioningVariant(PROVISIONING_VARIANT.PAYMENT_SUCCESS);
    setSelectedPlan("starter");
    // Dismissing wait must clear pendingSignup or hard refresh reopens this modal.
    resetSignupClientState({
      clearPending: true,
      clearForm: true,
      clearDraft: true,
    });
  };

  /** Send a new signup code. Held for 3 minutes after each send, and for the Cognito lock after too many attempts. */
  const handleResendSignUpOtp = async () => {
    if (signupOtpLocked) {
      setSignUpFeedback({
        type: "error",
        message: getOtpUserMessage({ code: SIGNUP_ERROR_CODES.OTP_LIMIT }),
      });
      return;
    }
    if (signupResendCooling) {
      setSignUpFeedback({
        type: "error",
        message: "You can request a new code in 3 minutes.",
      });
      return;
    }
    try {
      setSignUpLoading(true);
      await resendConfirmationCode({
        email: signUpForm.email.trim().toLowerCase(),
      });
      const resendAvailableAt = Date.now() + SIGNUP_OTP_RESEND_COOLDOWN_MS;
      setSignUpResendAvailableAt(resendAvailableAt);
      setSignUpOtp("");
      writeSignupDraft(signUpForm, selectedPlan, plansByName, {
        step: "otp",
        signupId,
        signupSessionToken,
        resendAvailableAt,
        otpLockedUntil: 0,
      });
      setSignUpFeedback({
        type: "success",
        message: "We sent a new verification code to your email.",
      });
    } catch (error) {
      await handleSignupApiError(error, {
        fallback: "Unable to resend code.",
        context: "resend",
      });
    } finally {
      setSignUpLoading(false);
    }
  };

  const handleDemoChange = (e) => {
    const { name, value } = e.target;
    setDemoForm((prev) => ({ ...prev, [name]: value }));
    setDemoErrors((prev) => ({ ...prev, [name]: "" }));
    if (demoFeedback.message) {
      setDemoFeedback({ type: "", message: "" });
    }
  };

  const validateDemoForm = () => {
    const errors = {};

    if (!demoForm.fullName.trim()) {
      errors.fullName = "Full name is required";
    } else if (demoForm.fullName.trim().length < 2) {
      errors.fullName = "Please enter your full name (min 2 characters)";
    }

    const workEmailError = getEmailError(demoForm.workEmail, "Work email");
    if (workEmailError) errors.workEmail = workEmailError;
    if (!demoForm.phoneNumber.trim())
      errors.phoneNumber = "Phone number is required";
    if (!demoForm.companyName.trim())
      errors.companyName = "Company name is required";
    if (!demoForm.companySize.trim())
      errors.companySize = "Please select your company size";

    return errors;
  };

  const openWhatsNewModal = (e) => {
    e.preventDefault();
    setShowWhatsNewModal(true);
  };

  const openRefundPolicyModal = (e) => {
    e.preventDefault();
    setShowRefundPolicyModal(true);
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    const errors = validateDemoForm();

    if (Object.keys(errors).length > 0) {
      setDemoErrors(errors);
      return;
    }

    try {
      setDemoSubmitting(true);
      setDemoFeedback({ type: "", message: "" });

      const payload = {
        product: "NOVA",
        ...demoForm,
        fullName: demoForm.fullName.trim(),
        workEmail: demoForm.workEmail.trim(),
        phoneNumber: demoForm.phoneNumber.trim(),
        companyName: demoForm.companyName.trim(),
        role: demoForm.role.trim(),
        challenge: demoForm.challenge.trim(),
      };

      const response = await axiosInstance.post("/request-demo", payload);

      const result = response.data || {};
      if (result.success === false) {
        setDemoFeedback({
          type: "error",
          message:
            result.message ||
            result.detail ||
            "Unable to submit your demo request right now.",
        });
        return;
      }

      localStorage.setItem(
        "novaDemoRequest",
        JSON.stringify({ ...payload, submittedAt: new Date().toISOString() }),
      );
      setDemoFeedback({
        type: "success",
        message:
          "Demo request submitted successfully. Our team will reach out within 24 hours.",
      });
      setDemoForm(INITIAL_DEMO_FORM);
      setDemoErrors({});
    } catch (error) {
      setDemoFeedback({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Unable to submit your demo request right now.",
        ),
      });
    } finally {
      setDemoSubmitting(false);
    }
  };

  return (
    <div className="nova-landing" data-testid="landing-page">
      {/* NAV */}
      <header
        id="nav"
        className="site-header"
        style={{ boxShadow: navShadow ? "0 2px 24px rgba(0,0,0,.35)" : "none" }}
      >
        <div className="container ni">
          <div className="nl">
            <img
              data-testid="site-nav-logo"
              src="https://nova.bizacuity.ai/nova-logo.png"
              alt="Nova"
              className="nl-logo"
            />
          </div>
          <nav className="nls">
            <a href="#aud" data-testid="site-nav-audience-link">Who&apos;s It For</a>
            <a href="#feat" data-testid="site-nav-features-link">Features</a>
            <a href="#ind" data-testid="site-nav-industries-link">Industries</a>
            <a href="#price" data-testid="site-nav-pricing-link">Pricing</a>
            <button type="button" className="nav-contact" onClick={openContactModal} data-testid="site-nav-contact-button">
              Contact
            </button>
          </nav>
          <div className="na">
            <button className="nsi" onClick={() => setShowSignInModal(true)} data-testid="site-nav-sign-in-link">
              Sign In
            </button>
            {/* <a className="nsi" href="#price" data-testid="site-nav-view-pricing-link">
              View Pricing
            </a> */}
          </div>
          <button className="hbg" onClick={toggleNav} aria-label="Toggle navigation" aria-expanded={mobileMenuOpen} data-testid="site-nav-mobile-menu-toggle-button">
            <svg viewBox="0 0 24 24">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
        <div id="mn" style={{ display: mobileMenuOpen ? "block" : "none" }}>
          <a href="#aud" onClick={toggleNav} data-testid="site-nav-mobile-audience-link">
            Who&apos;s It For
          </a>
          <a href="#feat" onClick={toggleNav} data-testid="site-nav-mobile-features-link">
            Features
          </a>
          <a href="#ind" onClick={toggleNav} data-testid="site-nav-mobile-industries-link">
            Industries
          </a>
          <a href="#price" onClick={toggleNav} data-testid="site-nav-mobile-pricing-link">
            Pricing
          </a>
          <button type="button" className="nav-contact mobile-nav-contact" onClick={openContactModal} data-testid="site-nav-mobile-contact-button">
            Contact
          </button>
          <div className="mobile-auth-actions">
            <button
              className="mobile-auth-button mobile-auth-secondary"
              onClick={() => {
                setMobileMenuOpen(false);
                setShowSignInModal(true);
              }}
              data-testid="site-nav-mobile-sign-in-link"
            >
              Sign In
            </button>
            {/* <a
              className="mobile-auth-button mobile-auth-secondary"
              href="#price"
              onClick={toggleNav}
              data-testid="site-nav-mobile-view-pricing-link"
            >
              View Pricing
            </a> */}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="hero">
        <div className="hg1"></div>
        <div className="hg2"></div>
        <div className="hi">
          {/* <div className="hbadge">
            <svg viewBox="0 0 24 24">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Enterprise AI Assistant
          </div> */}
          <h1 className="hh">
            NOVA: The AI that Knows
            <br />
            <em>Your Business, Inside Out</em>
          </h1>
          <p className="hp">
            Ask a question, get an answer pulled from your own documents, website, and code.
            <br />
            Every response cites the exact source it came from, so you can trust it or check it in one click.
          </p>
          <div className="hbtns">
            <button
              className="btn bb"
              onClick={() => setShowDemoModal(true)}
              data-testid="landing-hero-cta-button"
            >
              Book a Demo{" "}
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <button
              className="btn bw"
              onClick={() => openSignUpModal()}
              data-testid="landing-hero-secondary-cta-button"
            >
              Start Free
            </button>
          </div>
          <div className="htrust">
            <span className="dt"></span> Enterprise-grade security <span className="dt"></span> Built-in
            guardrails
          </div>
        </div>
      </section>

      {/* KNOWLEDGE CRISIS */}
      <section id="impact">
        <div className="container">
          <div className="ih sh rv" ref={addRef}>
            <span className="eye">The Knowledge Crisis</span>
            <h2 className="stitle">
              Every organization has a knowledge problem. Most just budget
              around it.
            </h2>
            <p className="ssub">
              The cost never shows up on an invoice — it shows up as lost
              hours, lost expertise, and quiet compliance drift.
            </p>
          </div>
          <div className="igrid rv" ref={addRef}>
            <div className="icard">
              <div className="inum">1.8 hrs</div>
              <div className="ilbl">Lost Per Employee, Daily</div>
              <div className="idesc">
                That's a full workday every week, per employee — not doing the
                work, just looking for what they need to do it.
                <br />
                <em>Source: McKinsey Global Institute</em>
              </div>
            </div>
            <div className="icard">
              <div className="inum">₹41.5 Cr</div>
              <div className="ilbl">Lost Annually Per 1,000 Employees</div>
              <div className="idesc">
                A massive budget leak hiding in plain sight — paying for lost
                productivity and duplicated work from bad search tools.
                <br />
                <em>Source: IDC</em>
              </div>
            </div>
            <div className="icard">
              <div className="inum">81%</div>
              <div className="ilbl">Interrupt a Colleague to Find Files</div>
              <div className="idesc">
                A continuous cycle of broken focus, every day, stopping two
                people from getting their actual jobs done.
                <br />
                <em>Source: Enterprise Search Survey</em>
              </div>
            </div>
          </div>
          <div className="igrid rv" ref={addRef}>
            <div className="icard">
              <div className="aico" style={{ margin: "0 auto 16px" }}>
                <svg viewBox="0 0 24 24">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <div className="ilbl">Scattered Information</div>
              <div className="idesc">
                Data hides across disconnected PDFs, emails, and shared
                drives. Conflicting versions turn basic search into an
                exhausting task.
              </div>
            </div>
            <div className="icard">
              <div className="aico" style={{ margin: "0 auto 16px" }}>
                <svg viewBox="0 0 24 24">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="18" y1="8" x2="23" y2="13" />
                  <line x1="23" y1="8" x2="18" y2="13" />
                </svg>
              </div>
              <div className="ilbl">Lost Expertise</div>
              <div className="idesc">
                Your senior people spend their days re-answering questions
                that are already documented somewhere. Then they leave, and
                the answers leave with them.
              </div>
            </div>
            <div className="icard">
              <div className="aico" style={{ margin: "0 auto 16px" }}>
                <svg viewBox="0 0 24 24">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <line x1="12" y1="8" x2="12" y2="13" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="ilbl">Compliance Drift</div>
              <div className="idesc">
                Critical policy versions and regulatory standards quietly
                diverge over time. Employees keep acting on the outdated one —
                until an audit notices.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section id="aud">
        <div className="container">
          <div className="ah sh rv" ref={addRef}>
            <span className="eye eye-case">Ask. Know. Act</span>
            <h2 className="stitle">Built for every conversation your business has</h2>
            <p className="ssub">
              Nova is a single answer engine pointed at your organization's
              knowledge. Three groups get value from it on day one.
            </p>
          </div>
          <div className="ag">
            <div className="ac int rv" ref={addRef}>
              <div className="aico">
                <svg viewBox="0 0 24 24">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="atag">Internal Teams</div>
              <h3>Employees get answers in seconds</h3>
              <p>
                Nova indexes your internal policies, product details, and
                SOPs. Employees ask questions directly in Google Chat and get
                clear answers with links to the source document, any hour of
                the day. Expandable to other environments like Meet and
                SharePoint as your workspace grows.
              </p>
            </div>
            <div className="ac int rv" ref={addRef}>
              <div className="aico">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
              <div className="atag">Website Visitors</div>
              <h3>Help visitors and close sales around the clock</h3>
              <p>
                Add a single script tag to deploy a widget on your site. Nova
                uses your public pages to answer pre-sales questions
                instantly, converting traffic into leads while deflecting
                support tickets.
              </p>
            </div>
            <div className="ac int rv" ref={addRef}>
              <div className="aico">
                <svg viewBox="0 0 24 24">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
              </div>
              <div className="atag">Engineering</div>
              <h3>Cut developer onboarding from weeks to days</h3>
              <p>
                Connect Nova to your GitHub repositories and it maps the
                architecture of your codebase. Engineers ask questions
                grounded in the source and get answers cited to the exact
                file, so new hires stop interrupting senior developers to
                learn how things work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="feat">
        <div className="container">
          <div className="fh sh rv" ref={addRef}>
            <span className="eye">Core Capabilities</span>
            <h2 className="stitle">What's actually in the product</h2>
            <p className="ssub">
              Three capabilities working together — so knowledge reaches everyone
              who needs it, safely and accountably.
            </p>
          </div>
          <div className="fg">
            {[
              {
                slug: "internal-knowledge",
                title: "Internal Knowledge Base",
                desc: "Nova unifies your internal documents, codebase, and team communications into one searchable layer. Employees ask in plain language and get answers with citations, no keyword guessing required.",
                color: "blu",
                items: [
                  "AI knowledge retrieval",
                  "Google Chat integration",
                  "GitHub repository indexing",
                  "Website knowledge widget",
                  "Expandable ecosystem",
                ],
              },
              // {
              //   slug: "website-ai-search",
              //   title: "Embeddable Website Widget",
              //   desc: "One script tag adds AI search to your site. Visitors get instant answers from your content without calling support.",
              //   color: "grn",
              //   items: [
              //     "Single-line embed",
              //     "Auto-indexes site content",
              //     "Reduces bounce & support load",
              //     "Branded & customisable",
              //   ],
              // },
              // {
              //   slug: "dev-onboarding",
              //   title: "Faster Developer Onboarding",
              //   desc: "New devs ask Nova about architecture, functions, and relationships — straight from your GitHub repos. Weeks become days.",
              //   color: "ind",
              //   items: [
              //     "GitHub repo & branch indexing",
              //     "Code-aware Q&A",
              //     "PR, issue & commit context",
              //     "3 weeks → 3 days ramp-up",
              //   ],
              // },
              // {
              //   slug: "google-chat-bot",
              //   title: "Google Chat Chatbot",
              //   desc: "Nova lives inside Google Chat — just like Meta's AI in WhatsApp. Ask questions and get answers without switching apps.",
              //   color: "tel",
              //   items: [
              //     "Native Google Chat bot",
              //     "Works in DMs & Spaces",
              //     "No app-switching required",
              //     "Zero training needed",
              //   ],
              // },
              {
                slug: "auditable-usage-analytics",
                title: "Auditable Usage & Analytics",
                desc: "Track search trends and interaction data through a live dashboard. Monitor what employees and customers are asking to see exactly which company guidelines are unclear or missing.",
                color: "blu",
                items: [
                  "Live question tracking",
                  "Content gap discovery",
                  "Full interaction logging",
                  "User activity dashboard",
                ],
              },
              {
                slug: "guardrails-safe-usage",
                title: "Guardrails & Safe Usage",
                desc: "Role-based permissions ensure employees only see documents within their authorized scope. Finance sees finance. HR sees HR. Every query and answer is logged with a timestamp.",
                color: "blu",
                items: [
                  "Role-based access control (RBAC)",
                  "Timestamped audit trails",
                  "Secure permission syncing",
                  "Department data isolation",
                ],
              },
            ].map((feature) => (
              <div
                key={feature.slug}
                id={`feat-${feature.slug}`}
                className={`fc rv${highlightedFeature === feature.slug ? " feature-highlight" : ""}`}
                ref={addRef}
              >
                <div className={`fic ${feature.color}`}>
                  <svg viewBox="0 0 24 24">
                    {feature.color === "blu" && (
                      <>
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                      </>
                    )}
                    {feature.color === "grn" && (
                      <>
                        <rect x="3" y="3" width="18" height="18" rx="3" />
                        <line x1="9" y1="9" x2="15" y2="9" />
                        <line x1="9" y1="12" x2="15" y2="12" />
                        <line x1="9" y1="15" x2="12" y2="15" />
                      </>
                    )}
                    {feature.color === "ind" && (
                      <>
                        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77A5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
                      </>
                    )}
                    {feature.color === "tel" && (
                      <>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </>
                    )}
                    {feature.color === "amb" && (
                      <>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </>
                    )}
                    {feature.color === "ros" && (
                      <>
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </>
                    )}
                  </svg>
                </div>
                <div className="ft">{feature.title}</div>
                <p className="fd">{feature.desc}</p>
                <ul className="fl">
                  {feature.items.map((item, j) => (
                    <li key={j} className="fi">
                      <svg viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="ind">
        <div className="container">
          <div className="fh sh rv" ref={addRef}>
            <span className="eye">Industries</span>
            <h2 className="stitle">
              Built for Organizations that Run on Documented Knowledge
            </h2>
            <p className="ssub">
              Nova delivers value across industries that live and die by
              their documentation.
            </p>
          </div>
          <div className="fg">
            {[
              {
                slug: "financial-services",
                title: "Financial Services",
                desc: "Indexes complex compliance frameworks, policy documents, and regulatory updates for instant retrieval. This reduces research time for risk teams and provides clean, human-readable audit trails for regulators.",
                icon: (
                  <>
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </>
                ),
              },
              {
                slug: "healthcare",
                title: "Healthcare",
                desc: "Medical and non-medical staff get immediate, accurate answers regarding clinical protocols and hospital administrative policies. This streamlines onboarding and ensures teams stay aligned with standardized care guidelines without manual document hunting.",
                icon: (
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                ),
              },
              {
                slug: "it-tech-services",
                title: "IT & Tech Services",
                desc: "Engineering wikis, incident runbooks, and internal SOPs are fully indexed for developer self-service. This shortens incident response times during outages and cuts down on repetitive support tickets for senior engineers.",
                icon: (
                  <>
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </>
                ),
              },
              {
                slug: "legal-consulting",
                title: "Legal & Consulting",
                desc: "Associates can instantly search through contract precedents, firm policies, and historical client knowledge bases. This accelerates contract review cycles and prevents teams from recreating existing legal work.",
                icon: (
                  <>
                    <path d="M12 3v18" />
                    <path d="M5 7l-3 6a3 3 0 0 0 6 0z" />
                    <path d="M19 7l-3 6a3 3 0 0 0 6 0z" />
                    <path d="M5 7h14" />
                    <path d="M8 3h8" />
                  </>
                ),
              },
              {
                slug: "retail-manufacturing",
                title: "Retail & Manufacturing",
                desc: "Floor workers and supervisors gain real-time access to safety manuals, quality standards, and production SOPs directly on the floor. This decreases training time for new hires and minimizes operational errors caused by outdated documentation.",
                icon: (
                  <>
                    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 0 1-8 0" />
                  </>
                ),
              },
              {
                slug: "education",
                title: "Education",
                desc: "The platform automates answers for student policy Q&A, faculty documentation, and complex administrative processes at scale. This lowers the volume of routine email inquiries to registrars and HR during peak enrollment periods.",
                icon: (
                  <>
                    <path d="M22 10 12 5 2 10l10 5 10-5z" />
                    <path d="M6 12v5c0 1.5 2.5 3 6 3s6-1.5 6-3v-5" />
                  </>
                ),
              },
            ].map((industry) => (
              <div key={industry.slug} className="fc rv" ref={addRef}>
                <div className="fic blu">
                  <svg viewBox="0 0 24 24">{industry.icon}</svg>
                </div>
                <div className="ft">{industry.title}</div>
                <p className="fd">{industry.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="price">
        <div className="container">
          <div className="prh sh rv" ref={addRef}>
            <span className="eye">Pricing</span>
            <h2 className="stitle">Simple, transparent pricing</h2>
            <p className="ssub">
              Start free, scale as you grow. No hidden fees.
            </p>
          </div>
          <div className="cnote">
            <span id="cdisplay">
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              Prices in {priceData.label}
            </span>
          </div>
          <div className="pgr">
            {isStarterPlanActive && (
            <div className="prc rv" ref={addRef}>
              <p className="ptier">Starter</p>
              <div className="pamt">
                <span className="pbig">Free</span>
              </div>
              <p className="ptag">30-day free trial, full access</p>
              <ul className="pfl">
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {formatUsersLimit(starterLimits ? starterLimits.users : 5)}
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {formatStorageLimit(starterLimits ? starterLimits.storage_bytes : 500 * 1024 * 1024)}
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {formatMessagesLimit(starterLimits ? starterLimits.ai_messages : 300)}
                </li>
                {/* <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Web chat widget
                </li> */}
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Basic audit logs
                </li>
              </ul>
              <button
                type="button"
                className="pb pbd"
                data-testid="landing-pricing-starter-cta-button"
                onClick={() => {
                  showPricingAlert("starter", "success", "Great choice! Redirecting you to create your free Starter account…");
                  openSignUpModal("starter");
                }}
              >
                Get Started Free
              </button>
            </div>
            )}
            {isProfessionalPlanActive && (
            <div className="prc ft rv" ref={addRef}>
              <div className="pop">Most Popular</div>
              <p className="ptier">Professional</p>
              <div className="pamt">
                <span className="pbig" id="pro-price">
                  {priceData.symbol}
                  {perUserPriceDisplay ?? priceData.price}
                </span>
                <span className="pmo">{perUserPriceDisplay ? "/ user / month" : "/ mo"}</span>
              </div>
              {perUserPriceDisplay && (
                <p className="pmin">
                  Minimum {professionalUsersLimit} users - Billed monthly - Exclusive of GST
                  {/* — {priceData.symbol}
                  {priceData.price}/mo + 18% GST = {priceData.symbol} */}
                  {/* {professionalGstTotalDisplay}/mo */}
                </p>
              )}
              {/* <p className="ptag">Billed monthly</p> */}
              <ul className="pfl">
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {professionalUsersLimit != null
                    ? `${professionalUsersLimit} users included`
                    : formatUsersLimit(professionalLimits ? professionalLimits.users : null)}
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {formatStorageLimit(professionalLimits ? professionalLimits.storage_bytes : 5 * 1024 ** 3)}
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {formatMessagesLimit(professionalLimits ? professionalLimits.ai_messages : 10000)}
                </li>
                {/* <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  GitHub integration
                </li> */}
                {/* <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Google Chat bot
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Website widget
                </li> */}
                {/* <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Guardrails
                </li> */}
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Cancel anytime - active until period end
                </li>
              </ul>
              {professionalUsersLimit != null && (
                <p className="pmore">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Need more than {professionalUsersLimit} users?{" "}
                  <button
                    type="button"
                    className="pmore-link"
                    data-testid="landing-pricing-professional-contact-link"
                    onClick={openContactModal}
                  >
                    Contact us
                  </button>
                </p>
              )}
              <button
                type="button"
                className="pb pbw"
                data-testid="landing-pricing-professional-cta-button"
                onClick={() => {
                  showPricingAlert("professional", "success", "Great choice! Let's get your Professional workspace set up…");
                  openSignUpModal("professional");
                }}
              >
                Purchase Pro
              </button>
            </div>
            )}
            {isEnterprisePlanActive && (
            <div className="prc rv" ref={addRef}>
              <p className="ptier">Enterprise</p>
              <div className="pamt">
                <span className="pbig">{isEnterprisePayPerUse ? "Pay per use" : "Custom"}</span>
              </div>
              <p className="ptag">
                {isEnterprisePayPerUse ? "Billed on usage - no fixed monthly fee" : "For large organisations"}
              </p>
              <ul className="pfl">
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Unlimited users &amp; storage
                </li>
                {/* <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Custom AI model selection
                </li> */}
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  SSO &amp; advanced RBAC
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Advanced guardrail policies
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Compliance-grade audit export
                </li>
                {/* <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  SLA &amp; dedicated support
                </li>
                <li className="pfi">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  On-premise option
                </li> */}
              </ul>
              <button
                type="button"
                className="pb pbd"
                data-testid="landing-pricing-enterprise-cta-button"
                onClick={(event) => {
                  showPricingAlert("enterprise", "info", "Connecting you with our sales team…");
                  openContactModal(event);
                }}
              >
                Contact Sales
              </button>
            </div>
            )}
          </div>
          {pricingAlert.message && (
            <div
              className={`pricing-alert ${pricingAlert.type}`}
              role="status"
              data-testid={`pricing-${pricingAlert.tier}-alert`}
            >
              <span>{pricingAlert.message}</span>
              <button
                type="button"
                className="pricing-alert-close"
                onClick={dismissPricingAlert}
                aria-label="Dismiss"
                data-testid={`pricing-${pricingAlert.tier}-alert-close-button`}
              >
                ×
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section id="cta">
        <div className="container">
          <h2 className="ctah">
            Your company already wrote the answers down. Stop making people
            hunt for them.
          </h2>
          <div className="ctab">
            <button className="btn bw" onClick={() => openSignUpModal()} data-testid="landing-cta-start-free-button">
              Start Free
            </button>
            <button className="btn bb" onClick={() => setShowDemoModal(true)} data-testid="landing-cta-request-demo-button">
              Book a Demo{" "}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="foot">
        <div className="container">
          <div className="fg2">
            <div className="fb">
              <img
                data-testid="landing-footer-logo"
                src="https://nova.bizacuity.ai/nova-logo.png"
                alt="Nova"
                style={{
                  height: "30px",
                  filter: "brightness(0) invert(1)",
                  opacity: 0.8,
                }}
              />
              <p>
                AI that knows your business — giving employees, customers, and
                developers the right answers, instantly.
              </p>
            </div>
            <div className="fc2">
              <h4>Product</h4>
              <ul>
                <li>
                  <a href="#feat" data-testid="landing-footer-features-link">Features</a>
                </li>
                <li>
                  <a href="#price" data-testid="landing-footer-pricing-link">Pricing</a>
                </li>
                <li>
                  <button
                    type="button"
                    className="footer-contact"
                    onClick={openWhatsNewModal}
                    data-testid="landing-footer-whats-new-link"
                  >
                    What&apos;s New
                  </button>
                </li>
              </ul>
            </div>
            <div className="fc2">
              <h4>Use Cases</h4>
              <ul>
                {USE_CASES.map(({ label, slug }) => (
                  <li key={slug}>
                    <a href={`#feat-${slug}`} data-testid={`landing-footer-use-case-${slug}-link`}>{label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="fc2">
              <h4>Legal</h4>
              <ul>
                <li>
                  <a href="/privacypolicy" data-testid="landing-footer-privacy-policy-link">Privacy Policy</a>
                </li>
                <li>
                  <button
                    type="button"
                    className="footer-contact"
                    onClick={openRefundPolicyModal}
                    data-testid="landing-footer-refund-policy-link"
                  >
                    Refund Policy
                  </button>
                </li>
                <li>
                  <a href="/termsandconditions" data-testid="landing-footer-terms-link">Terms &amp; Conditions</a>
                </li>
                <li>
                  <button type="button" className="footer-contact" onClick={openContactModal} data-testid="landing-footer-contact-button">
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="fbot">
            <p className="fcp">
              © {new Date().getFullYear()} Nova by BizAcuity. All rights
              reserved.
            </p>
            <div className="fsoc">
              <a
                href="https://in.linkedin.com/company/bizacuity-solutions-pvt-ltd-"
                target="_blank"
                rel="noreferrer"
                data-testid="landing-footer-linkedin-link"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal open={showContactModal} onClose={closeContactModal} />

      {/* RELEASE NOTES MODAL */}
      {showWhatsNewModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowWhatsNewModal(false)}
        >
          <div
            className="modal-content changelog-modal whats-new-modal release-notes-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-whats-new-modal"
          >
            <button
              className="modal-close"
              onClick={() => setShowWhatsNewModal(false)}
              data-testid="landing-whats-new-close-button"
            >
              <CloseOutlinedIcon />
            </button>
            <span className="changelog-kicker">Release notes</span>
            <h2 className="modal-title">{RELEASE_NOTES_VERSION} is here</h2>
            <p className="modal-subtitle whats-new-intro">
              A major milestone toward an enterprise-ready AI platform — secure
              multi-tenancy, a centralized knowledge base, enhanced AI
              capabilities, and a streamlined employee onboarding experience.
            </p>

            <div className="whats-new-meta">
              <span className="whats-new-count">{RELEASE_NOTES_VERSION}</span>
              <time className="whats-new-latest">
                Released {RELEASE_NOTES_DATE}
              </time>
            </div>

            <div className="release-notes-glance">
              {RELEASE_NOTES_GLANCE.map((item) => (
                <div className="release-notes-glance-item" key={item.title}>
                  <p className="release-notes-glance-title">{item.title}</p>
                  <p className="release-notes-glance-summary">{item.summary}</p>
                </div>
              ))}
            </div>

            <h3 className="release-notes-section-title">New Features</h3>
            {RELEASE_NOTES_FEATURES.map((feature) => (
              <div className="release-notes-feature" key={feature.title}>
                <h4 className="release-notes-feature-title">{feature.title}</h4>
                <p className="release-notes-feature-intro">{feature.intro}</p>
                <ul className="release-notes-list">
                  {feature.bullets.map((bullet) => (
                    <li key={bullet}>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <h3 className="release-notes-section-title">Coming Soon</h3>
            <div className="release-notes-coming-soon">
              {RELEASE_NOTES_COMING_SOON.map((item) => (
                <div className="release-notes-soon-item" key={item.title}>
                  <span className="release-notes-soon-badge">Soon</span>
                  <p className="release-notes-soon-title">{item.title}</p>
                  <p className="release-notes-soon-summary">{item.summary}</p>
                </div>
              ))}
            </div>

            <div className="release-notes-closing">
              <p>
                Thank you for being part of the NOVA journey. We&apos;re
                committed to continuously enhancing the platform and
                delivering innovative AI-powered capabilities that help
                organizations work smarter.
              </p>
              <p className="release-notes-signoff">- The NOVA Team</p>
            </div>
          </div>
        </div>
      )}

      {/* REFUND POLICY MODAL */}
      {showRefundPolicyModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowRefundPolicyModal(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-refund-policy-modal"
          >
            <button
              className="modal-close"
              onClick={() => setShowRefundPolicyModal(false)}
              data-testid="landing-refund-policy-close-button"
            >
              <CloseOutlinedIcon />
            </button>
            <h2 className="modal-title">Refund Policy</h2>
            <p className="modal-subtitle">
              No refunds are issued once a payment is successfully processed - if a transaction fails, your bank will process and return the amount.
            </p>
          </div>
        </div>
      )}

      {/* SIGN IN MODAL */}
      {showSignInModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSignInModal(false)}
        >
          <div
            className="modal-content sign-in-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-sign-in-modal"
          >
            <button
              className="modal-close"
              onClick={() => setShowSignInModal(false)}
              data-testid="landing-sign-in-close-button"
            >
              <CloseOutlinedIcon />
            </button>
            <div className="auth-split-modal">
              <aside className="auth-brand-panel">
                <img
                  data-testid="landing-signin-modal-logo"
                  src="https://nova.bizacuity.ai/nova-logo.png"
                  alt="Nova"
                  className="auth-brand-logo"
                />
                <span className="auth-brand-accent"></span>
                <h3>Welcome back</h3>
                <p>Sign in to continue managing your Nova workspace.</p>
              </aside>

              <div className="auth-form-panel">
                <h2 className="modal-title">Sign in</h2>
                <p className="modal-subtitle">
                  Use your email and password.
                </p>

                <form className="sign-in-form" onSubmit={handleExternalLogin} noValidate>
                  <div className="form-group">
                    <label className="form-label">Email address</label>
                    <div className="auth-field">
                      <EmailOutlined className="auth-field-icon" />
                      <input
                        type="email"
                        name="email"
                        className="form-input"
                        placeholder="name@company.com"
                        value={signInForm.email}
                        onChange={handleSignInChange}
                        data-testid="landing-sign-in-email-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div className="auth-field auth-field-password">
                      <LockOutlined className="auth-field-icon" />
                      <input
                        type={showSignInPassword ? "text" : "password"}
                        name="password"
                        className="form-input"
                        placeholder="Enter your password"
                        value={signInForm.password}
                        onChange={handleSignInChange}
                        maxLength={PASSWORD_MAX_LENGTH}
                        data-testid="landing-sign-in-password-input"
                      />
                      <AuthPasswordToggle
                        show={showSignInPassword}
                        onClick={() =>
                          setShowSignInPassword(!showSignInPassword)
                        }
                        testId="landing-sign-in-password-toggle-button"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="text-link-button"
                    onClick={openForgotPassword}
                    data-testid="landing-forgot-password-button"
                  >
                    Forgot password?
                  </button>

                  {signInError ? (
                    <p className="sign-in-error" data-testid="landing-sign-in-error-banner">{signInError}</p>
                  ) : null}
                  {signInSuccess ? (
                    <p className="sign-in-success" data-testid="landing-sign-in-success-banner">{signInSuccess}</p>
                  ) : null}

                  <button
                    type="submit"
                    className="auth-action primary"
                    disabled={signInLoading || googleSignInLoading}
                    data-testid="landing-sign-in-submit-button"
                  >
                    {signInLoading ? "Signing in..." : "Sign in"}
                  </button>
                </form>

                {/* <div className="sign-in-divider">
                  <span>or continue with</span>
                </div>

                <div className="sign-in-actions">
                  <button
                    type="button"
                    className="auth-action google"
                    onClick={handleGoogleLogin}
                    disabled={signInLoading || googleSignInLoading}
                  >
                    <img
                      data-testid="landing-signin-google-icon"
                      src="/google-logo.png"
                      alt="Google"
                      width={20}
                      height={20}
                    />
                    {googleSignInLoading
                      ? "Opening Google..."
                      : "Continue with Google"}
                  </button>
                </div>
                <p className="sign-in-note">
                  Google sign-in redirects through the secure Cognito Hosted UI.
                </p> */}

                <div className="form-toggle">
                  Don't have an account?{" "}
                  <button
                    onClick={() => {
                      setShowSignInModal(false);
                      openSignUpModal();
                    }}
                    data-testid="landing-sign-in-switch-to-sign-up-button"
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PASSWORD RESET MODAL */}
      {showPasswordResetModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowPasswordResetModal(false)}
        >
          <div
            className="modal-content password-reset-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-password-reset-modal"
          >
            <button
              className="modal-close"
              onClick={() => setShowPasswordResetModal(false)}
              data-testid="landing-password-reset-close-button"
            >
              <CloseOutlinedIcon />
            </button>
            {passwordResetStep !== "success" && (
              <>
                <ResetStepper
                  activeStep={passwordResetStep === "reset" ? 1 : 0}
                />
                <h2 className="modal-title">Reset Password</h2>
                <p className="modal-subtitle">
                  {passwordResetStep === "email"
                    ? "Enter your email to receive a verification code."
                    : "Enter the OTP from your email and choose a new password."}
                </p>
                {passwordResetStep === "reset" ? (
                  <p className="reset-email-display">{passwordResetForm.email}</p>
                ) : null}
              </>
            )}

            {passwordResetStep === "email" && (
              <form
                className="sign-in-form"
                onSubmit={handleForgotPasswordSubmit}
                noValidate
              >
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <div className="auth-field">
                    <EmailOutlined className="auth-field-icon" />
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      placeholder="name@company.com"
                      value={passwordResetForm.email}
                      onChange={handlePasswordResetChange}
                      data-testid="landing-password-reset-email-input"
                    />
                  </div>
                </div>
                {signInError ? (
                  <p className="sign-in-error" data-testid="landing-password-reset-error-banner">{signInError}</p>
                ) : null}
                {signInSuccess ? (
                  <p className="sign-in-success" data-testid="landing-password-reset-success-banner">{signInSuccess}</p>
                ) : null}
                <button
                  type="submit"
                  className="auth-action primary"
                  disabled={passwordResetLoading}
                  data-testid="landing-password-reset-send-otp-button"
                >
                  {passwordResetLoading ? "Sending OTP..." : "Send OTP"}
                </button>
                <button
                  type="button"
                  className="auth-back-link"
                  disabled={passwordResetLoading}
                  onClick={backToSignIn}
                  data-testid="landing-password-reset-back-to-sign-in-button"
                >
                  ← Back to Sign In
                </button>
              </form>
            )}

            {passwordResetStep === "reset" && (
              <form
                className="sign-in-form"
                onSubmit={handleResetPasswordSubmit}
                noValidate
              >
                <input
                  type="hidden"
                  name="email"
                  value={passwordResetForm.email}
                  data-testid="landing-password-reset-confirm-email-input"
                  readOnly
                />
                <div className="form-group">
                  <label className="form-label">OTP</label>
                  <div data-testid="landing-password-reset-otp-input">
                    <OtpDigitInput
                      value={passwordResetForm.otp}
                      onChange={(otp) => {
                        setPasswordResetForm((prev) => ({ ...prev, otp }));
                        if (signInError) setSignInError("");
                        if (signInSuccess) setSignInSuccess("");
                      }}
                      disabled={passwordResetLoading || resetOtpExpired}
                      testIdPrefix="landing-password-reset-otp-digit-input"
                    />
                  </div>
                  {!resetOtpExpired && resetOtpExpiresAt && (
                    <CountdownTimer
                      expiresAt={resetOtpExpiresAt}
                      onComplete={() => {
                        setResetOtpExpired(true);
                        setSignInError("This code has expired. Please request a new one.");
                      }}
                    />
                  )}
                  {resetOtpExpired && (
                    <p
                      className="reset-otp-expired"
                      data-testid="landing-password-reset-otp-expired-banner"
                    >
                      This code has expired. Click Resend OTP to get a new one.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">New password</label>
                  <div className="auth-field auth-field-password">
                    <LockOutlined className="auth-field-icon" />
                    <input
                      type={showResetPassword ? "text" : "password"}
                      name="newPassword"
                      className="form-input"
                      placeholder="Enter your new password"
                      value={passwordResetForm.newPassword}
                      onChange={handlePasswordResetChange}
                      maxLength={PASSWORD_MAX_LENGTH}
                      autoComplete="new-password"
                      data-testid="landing-password-reset-new-password-input"
                    />
                    <AuthPasswordToggle
                      show={showResetPassword}
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      disabled={passwordResetLoading}
                      testId="landing-password-reset-password-toggle-button"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm password</label>
                  <div className="auth-field auth-field-password">
                    <LockOutlined className="auth-field-icon" />
                    <input
                      type={showResetConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      className="form-input"
                      placeholder="Confirm your new password"
                      value={passwordResetForm.confirmPassword}
                      onChange={handlePasswordResetChange}
                      maxLength={PASSWORD_MAX_LENGTH}
                      autoComplete="new-password"
                      data-testid="landing-password-reset-confirm-password-input"
                    />
                    <AuthPasswordToggle
                      show={showResetConfirmPassword}
                      onClick={() =>
                        setShowResetConfirmPassword(!showResetConfirmPassword)
                      }
                      disabled={passwordResetLoading}
                      testId="landing-password-reset-confirm-password-toggle-button"
                    />
                  </div>
                </div>
                {signInError ? (
                  <p className="sign-in-error" data-testid="landing-password-reset-error-banner">{signInError}</p>
                ) : null}
                {signInSuccess ? (
                  <p className="sign-in-success" data-testid="landing-password-reset-success-banner">{signInSuccess}</p>
                ) : null}
                <button
                  type="submit"
                  className="auth-action primary"
                  disabled={
                    passwordResetLoading ||
                    resetOtpExpired ||
                    passwordResetForm.otp.length !== 6
                  }
                  data-testid="landing-password-reset-submit-button"
                >
                  {passwordResetLoading ? "Resetting..." : "Reset Password"}
                </button>
                <div className="reset-footer-links">
                  <button
                    type="button"
                    className="auth-back-link"
                    disabled={passwordResetLoading}
                    onClick={() => {
                      clearOtpTimer("reset");
                      setPasswordResetStep("email");
                      setSignInError("");
                      setSignInSuccess("");
                      setResetOtpExpired(false);
                      setPasswordResetForm((prev) => ({
                        ...prev,
                        otp: "",
                        newPassword: "",
                        confirmPassword: "",
                      }));
                    }}
                    data-testid="landing-password-reset-back-to-email-button"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    className="text-link-button"
                    onClick={(e) => {
                      if (!resetOtpExpired) {
                        setSignInError(
                          "You can request a new code after the current one expires.",
                        );
                        return;
                      }
                      setPasswordResetForm((prev) => ({ ...prev, otp: "" }));
                      handleForgotPasswordSubmit(e);
                    }}
                    disabled={passwordResetLoading || !resetOtpExpired}
                    data-testid="landing-password-reset-resend-otp-button"
                  >
                    Resend OTP
                  </button>
                </div>
              </form>
            )}

            {passwordResetStep === "success" && (
              <div className="reset-success-panel" data-testid="landing-password-reset-success-modal">
                <CheckCircle sx={{ fontSize: 56, color: "#10B981", mb: 1.5 }} />
                <h2 className="modal-title">Password Reset Successful</h2>
                <p className="modal-subtitle">
                  Your password has been updated. You can now sign in with your
                  new password.
                </p>
                <button
                  type="button"
                  className="auth-action primary"
                  onClick={backToSignIn}
                  data-testid="landing-password-reset-success-signin-button"
                >
                  Sign In
                  <ArrowForward sx={{ fontSize: 18, ml: 1 }} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SIGN UP MODAL */}
      {showSignUpModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSignUpModal(false)}
        >
          <div
            className="modal-content signup-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-sign-up-modal"
          >
            <button
              className="modal-close"
              onClick={() => setShowSignUpModal(false)}
              data-testid="landing-sign-up-close-button"
            >
              ×
            </button>
            <div className="auth-split-modal auth-signup-split">
              <aside className="auth-brand-panel">
                <img
                  data-testid="landing-signup-modal-logo"
                  src="https://nova.bizacuity.ai/nova-logo.png"
                  alt="Nova"
                  className="auth-brand-logo"
                />
                <span className="auth-brand-accent"></span>
                <h3>Start with Nova</h3>
                <p>
                  {marketplaceSession
                    ? "Your Marketplace subscription is linked. Finish signup to open your workspace."
                    : "Create your workspace, verify your email, and invite your team."}
                </p>
              </aside>

              <div className="auth-form-panel auth-signup-panel">
                <h2 className="modal-title">Get Started</h2>
                <p className="modal-subtitle">
                  {marketplaceSession
                    ? "Complete your account setup for your AWS Marketplace subscription."
                    : "Create your Nova account and verify your email."}
                </p>
                <div
                  data-testid="landing-sign-up-selected-plan-badge"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 12px",
                    borderRadius: "999px",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "14px",
                    background: landingSemantic.status.brand.bg,
                    color: brandColors.primary,
                  }}
                >
                  Plan selected: {selectedPlan === "professional" ? "Professional" : "Starter"}
                </div>
                <div className="auth-signup-scroll">
                  {marketplaceSession && (
                    <div
                      className="demo-feedback success"
                      data-testid="landing-marketplace-subscription-banner"
                    >
                      Your AWS Marketplace subscription is ready. Create your
                      Nova admin account to finish onboarding.
                    </div>
                  )}
                  {signUpFeedback.message && (
                    <div className={`demo-feedback ${signUpFeedback.type}`} data-testid="landing-sign-up-feedback-banner">
                      {signUpFeedback.message}
                    </div>
                  )}
                  {signUpStep === "resume-payment" && (
                    <form
                      onSubmit={handleResumePaymentSubmit}
                      style={{ marginTop: "8px" }}
                      noValidate
                      data-testid="landing-sign-up-resume-payment-form"
                    >
                      <p
                        style={{
                          color: landingSemantic.text.muted,
                          fontSize: "14px",
                          lineHeight: 1.6,
                          marginBottom: "16px",
                        }}
                      >
                        Enter the email and password from your signup to continue
                        to plan selection and payment. Profile details stay locked.
                      </p>
                      <div className="form-group">
                        <label className="form-label">
                          Email Address <span className="required">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-input"
                          placeholder="you@example.com"
                          value={resumePaymentForm.email}
                          onChange={(e) =>
                            setResumePaymentForm((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          required
                          style={signupInputStyle("email")}
                          data-testid="landing-sign-up-resume-email-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Password <span className="required">*</span>
                        </label>
                        <div className="password-input-group">
                          <input
                            type={showResumePassword ? "text" : "password"}
                            className="form-input"
                            placeholder="••••••••"
                            value={resumePaymentForm.password}
                            onChange={(e) =>
                              setResumePaymentForm((prev) => ({
                                ...prev,
                                password: e.target.value,
                              }))
                            }
                            required
                            style={signupInputStyle("password")}
                            data-testid="landing-sign-up-resume-password-input"
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() =>
                              setShowResumePassword(!showResumePassword)
                            }
                            title={
                              showResumePassword ? "Hide password" : "Show password"
                            }
                            data-testid="landing-sign-up-resume-password-toggle"
                          >
                            {showResumePassword ? "Hide" : "Show"}
                          </button>
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="form-submit"
                        disabled={signUpLoading}
                        data-testid="landing-sign-up-resume-payment-submit"
                      >
                        {signUpLoading ? "Please wait..." : "Continue to Payment"}
                      </button>
                    </form>
                  )}
                  {signUpStep === "details" && (
                    <form onSubmit={handleSignUpSubmit} noValidate>
                      <div className="form-row">
                        <div style={{ flex: 1, display: "flex", gap: "12px" }}>
                          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                            <label className="form-label">First Name <span className="required">*</span></label>
                            <input
                              type="text"
                              name="firstName"
                              className="form-input"
                              placeholder="John"
                              value={signUpForm.firstName}
                              onChange={handleSignUpChange}
                              onBlur={handleSignUpBlur}
                              maxLength={SIGNUP_NAME_MAX_LENGTH}
                              required
                              disabled={signupLocked}
                              style={signupInputStyle("firstName")}
                              data-testid="landing-sign-up-first-name-input"
                            />
                            {signupFieldErrors.firstName ? (
                              <p className="form-error">{signupFieldErrors.firstName}</p>
                            ) : null}
                          </div>
                          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                            <label className="form-label">Last Name <span className="required">*</span></label>
                            <input
                              type="text"
                              name="lastName"
                              className="form-input"
                              placeholder="Doe"
                              value={signUpForm.lastName}
                              onChange={handleSignUpChange}
                              onBlur={handleSignUpBlur}
                              maxLength={SIGNUP_NAME_MAX_LENGTH}
                              required
                              disabled={signupLocked}
                              style={signupInputStyle("lastName")}
                              data-testid="landing-sign-up-last-name-input"
                            />
                            {signupFieldErrors.lastName ? (
                              <p className="form-error">{signupFieldErrors.lastName}</p>
                            ) : null}
                          </div>
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label className="form-label">
                            Organization Name <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            name="organizationName"
                            className="form-input"
                            placeholder="XYZ solutions"
                            value={signUpForm.organizationName}
                            onChange={handleSignUpChange}
                            onBlur={handleSignUpBlur}
                            required
                            disabled={signupLocked}
                            style={signupInputStyle("organizationName")}
                            data-testid="landing-sign-up-organization-input"
                          />
                          {signupFieldErrors.organizationName ? (
                            <p className="form-error">{signupFieldErrors.organizationName}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                          <label className="gstin-checkbox" htmlFor="signup-gstin-toggle">
                            <input
                              id="signup-gstin-toggle"
                              type="checkbox"
                              className="gstin-checkbox-input"
                              checked={hasGstin}
                              onChange={handleGstinCheckboxChange}
                              onKeyDown={handleGstinCheckboxKeyDown}
                              disabled={signupLocked}
                              data-testid="landing-sign-up-gstin-checkbox"
                            />
                            <span className="gstin-checkbox-text">Add GSTIN</span>
                          </label>
                          {hasGstin && (
                            <>
                              <input
                                type="text"
                                name="gstin"
                                className="form-input"
                                placeholder="22AAAAA0000A1ZC"
                                value={signUpForm.gstin}
                                onChange={handleGstinChange}
                                onBlur={handleGstinBlur}
                                maxLength={15}
                                disabled={signupLocked}
                                style={{ marginTop: "8px", textTransform: "uppercase" }}
                                data-testid="landing-sign-up-gstin-input"
                              />
                              {gstinError ? (
                                <p className="form-error">{gstinError}</p>
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label className="form-label">URL Prefix <span className="required">*</span></label>
                          <input
                            type="text"
                            name="subdomain"
                            className="form-input"
                            placeholder="mycompany"
                            value={signUpForm.subdomain}
                            onChange={handleSignUpChange}
                            onBlur={handleSignUpBlur}
                            maxLength={SIGNUP_SUBDOMAIN_MAX_LENGTH}
                            required
                            disabled={signupLocked}
                            style={signupInputStyle("subdomain")}
                            data-testid="landing-sign-up-subdomain-input"
                          />
                          {subdomainError || signupFieldErrors.subdomain ? (
                            <p
                              className="form-error"
                              style={{ fontWeight: 600 }}
                              data-testid="landing-sign-up-subdomain-error"
                            >
                              {subdomainError || signupFieldErrors.subdomain}
                            </p>
                          ) : subdomainChecking ? (
                            <p
                              style={{
                                marginTop: "8px",
                                marginBottom: 0,
                                fontSize: "12px",
                                color: landingSemantic.text.secondary,
                              }}
                            >
                              Checking availability…
                            </p>
                          ) : subdomainAvailable ? (
                            <p
                              style={{
                                marginTop: "8px",
                                fontSize: "12px",
                                color: brandColors.primary,
                              }}
                            >
                              This workspace URL is available.
                            </p>
                          ) : null}
                          {/* Hide preview URL when taken or invalid — avoid implying it is usable. */}
                          {allowedDomain &&
                            subdomainAvailable !== false &&
                            !subdomainError &&
                            !signupFieldErrors.subdomain && (
                            <p
                              style={{
                                marginTop: "8px",
                                fontSize: "12px",
                                color: landingSemantic.text.secondary,
                                fontStyle: "italic",
                              }}
                            >
                              Your URL:{" "}
                              <strong>https://{allowedDomain}/</strong>
                            </p>
                          )}
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label className="form-label">Region <span className="required">*</span></label>
                          <select
                            name="region"
                            className="form-input"
                            value={signUpForm.region}
                            onChange={handleSignUpChange}
                            onBlur={handleSignUpBlur}
                            required
                            disabled={signupLocked}
                            style={signupInputStyle("region")}
                            data-testid="landing-sign-up-region-input"
                          >
                            <option value="">Select your region</option>
                            {REGION_OPTIONS.map((region) => (
                              <option key={region.code} value={region.code}>
                                {region.dialCode
                                  ? `${region.name} (${region.dialCode})`
                                  : region.name}
                              </option>
                            ))}
                          </select>
                          {signupFieldErrors.region ? (
                            <p className="form-error">{signupFieldErrors.region}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label className="form-label">Email Address <span className="required">*</span></label>
                          <input
                            type="email"
                            name="email"
                            className="form-input"
                            placeholder="you@example.com"
                            value={signUpForm.email}
                            onChange={handleSignUpChange}
                            onBlur={handleSignUpBlur}
                            required
                            disabled={signupLocked}
                            style={signupInputStyle("email")}
                            data-testid="landing-sign-up-email-input"
                          />
                          {signupFieldErrors.email ? (
                            <p className="form-error">{signupFieldErrors.email}</p>
                          ) : null}
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label className="form-label">Phone Number <span className="required">*</span></label>
                          <div
                            className="phone-input-group"
                            style={signupInputStyle("phoneNumber")}
                          >
                            {signupDialCode ? (
                              <span className="phone-dial-prefix" aria-hidden="true">
                                {signupDialCode}
                              </span>
                            ) : null}
                            <input
                              type="tel"
                              name="phoneNumber"
                              className="form-input"
                              placeholder={
                                signUpForm.region
                                  ? "Phone number"
                                  : "Select a region first"
                              }
                              value={signupPhoneNational}
                              onChange={handleSignUpChange}
                              onBlur={handleSignUpBlur}
                              required
                              disabled={signupLocked || !signUpForm.region}
                              inputMode="numeric"
                              autoComplete="tel-national"
                              data-testid="landing-sign-up-phone-input"
                            />
                          </div>
                          {signupFieldErrors.phoneNumber ? (
                            <p className="form-error">{signupFieldErrors.phoneNumber}</p>
                          ) : null}
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label className="form-label">Password <span className="required">*</span></label>
                          <div className="password-input-group">
                            <input
                              type={showSignUpPassword ? "text" : "password"}
                              name="password"
                              className="form-input"
                              placeholder="••••••••"
                              value={signUpForm.password}
                              onChange={handleSignUpChange}
                              onBlur={handleSignUpBlur}
                              maxLength={PASSWORD_MAX_LENGTH}
                              required
                              style={signupInputStyle("password")}
                              data-testid="landing-sign-up-password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() =>
                                setShowSignUpPassword(!showSignUpPassword)
                              }
                              title={
                                showSignUpPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                              data-testid="landing-sign-up-password-toggle-button"
                            >
                              {showSignUpPassword ? (
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              ) : (
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                  <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                              )}
                            </button>
                          </div>
                          {signupFieldErrors.password ? (
                            <p className="form-error">{signupFieldErrors.password}</p>
                          ) : null}
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label className="form-label">Confirm Password <span className="required">*</span></label>
                          <div className="password-input-group">
                            <input
                              type={
                                showSignUpConfirmPassword ? "text" : "password"
                              }
                              name="confirmPassword"
                              className="form-input"
                              placeholder="••••••••"
                              value={signUpForm.confirmPassword}
                              onChange={handleSignUpChange}
                              onBlur={handleSignUpBlur}
                              maxLength={PASSWORD_MAX_LENGTH}
                              required
                              style={signupInputStyle("confirmPassword")}
                              data-testid="landing-sign-up-confirm-password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() =>
                                setShowSignUpConfirmPassword(
                                  !showSignUpConfirmPassword,
                                )
                              }
                              title={
                                showSignUpConfirmPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                              data-testid="landing-sign-up-confirm-password-toggle-button"
                            >
                              {showSignUpConfirmPassword ? (
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              ) : (
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                  <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                              )}
                            </button>
                          </div>
                          {signupFieldErrors.confirmPassword ? (
                            <p className="form-error">{signupFieldErrors.confirmPassword}</p>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="form-submit"
                        disabled={
                          signUpLoading ||
                          !isSignupDetailsValid ||
                          subdomainChecking ||
                          subdomainAvailable === false ||
                          Boolean(subdomainError)
                        }
                        data-testid="landing-sign-up-submit-button"
                      >
                        {signUpLoading
                          ? signupId
                            ? "Updating..."
                            : "Sending OTP..."
                          : signupId
                            ? "Update & Continue"
                            : "Verify Email"}
                      </button>
                    </form>
                  )}
                  {signUpStep === "otp" && (
                    <form
                      onSubmit={handleVerifySignUpOtp}
                      style={{ marginTop: "18px" }}
                      noValidate
                    >
                      <p
                        style={{
                          color: landingSemantic.text.muted,
                          fontSize: "14px",
                          lineHeight: 1.6,
                          marginBottom: "16px",
                        }}
                      >
                        Confirm the code sent to{" "}
                        <strong>{signUpForm.email.trim().toLowerCase()}</strong>
                        .
                      </p>
                      <div className="form-group">
                        <label className="form-label" htmlFor="signup-verification-code">Verification code <span className="required">*</span></label>
                        <input
                          type="text"
                          className="form-input"
                          id="signup-verification-code"
                          placeholder="Enter the 6-digit code"
                          inputMode="numeric"
                          maxLength={6}
                          value={signUpOtp}
                          onChange={(e) => setSignUpOtp(e.target.value)}
                          disabled={signUpLoading || signupOtpLocked}
                          required
                          data-testid="landing-sign-up-otp-input"
                        />
                        {signupOtpLocked && (
                          <p style={{ fontSize: "13px", color: brandColors.error, textAlign: "center", margin: "6px 0 0" }}>
                            Too many attempts. Verify and resend stay paused for about 15 minutes.
                          </p>
                        )}
                      </div>
                      <button
                        type="submit"
                        className="form-submit"
                        disabled={signUpLoading || signupOtpLocked}
                        data-testid="landing-sign-up-verify-otp-button"
                      >
                        {signUpLoading ? "Verifying..." : "Verify Email & Continue"}
                      </button>
                      <button
                        type="button"
                        className="form-submit"
                        onClick={handleResendSignUpOtp}
                        disabled={signUpLoading || signupOtpLocked || signupResendCooling}
                        style={{
                          marginTop: "10px",
                          background: landingSemantic.status.brand.bg,
                          color: brandColors.primary,
                        }}
                        data-testid="landing-sign-up-resend-otp-button"
                      >
                        Resend OTP
                      </button>
                      {!signupOtpLocked && signupResendCooling && (
                        <p style={{ fontSize: "13px", color: landingSemantic.text.muted, textAlign: "center", margin: "6px 0 0" }}>
                          Code expires in{" "}
                          <strong>
                            {formatOtpCountdown(
                              Math.ceil(
                                ((signUpResendAvailableAt || 0) - signupOtpClock) / 1000,
                              ),
                            )}
                          </strong>
                          . You can request another code after that.
                        </p>
                      )}
                      {signupCodeExpired && (
                        <p style={{ fontSize: "13px", color: brandColors.error, textAlign: "center", margin: "6px 0 0" }}>
                          This code has expired. Request a new code.
                        </p>
                      )}
                      <button
                        type="button"
                        className="form-submit"
                        onClick={handleEditSignupDetails}
                        disabled={signUpLoading || signupLocked}
                        style={{
                          marginTop: "10px",
                          background: landingSemantic.surface.header,
                          color: landingSemantic.text.muted,
                        }}
                        data-testid="landing-sign-up-edit-details-button"
                      >
                        Edit signup details
                      </button>
                    </form>
                  )}
                  {signUpStep === "payment" && (
                    <div style={{ marginTop: "18px" }}>
                      {!signupOrder ? (
                        <>
                          <p
                            style={{
                              color: landingSemantic.text.muted,
                              fontSize: "14px",
                              lineHeight: 1.6,
                              marginBottom: "16px",
                            }}
                          >
                            Email verified! Continue to payment to start provisioning your{" "}
                            {selectedPlan === "professional" ? "Professional" : "Starter"}{" "}
                            workspace. You can sign in after setup completes.
                          </p>
                          <button
                            type="button"
                            className="form-submit"
                            onClick={handleContinueToPayment}
                            disabled={signUpLoading}
                            data-testid="landing-sign-up-continue-to-payment-button"
                          >
                            {signUpLoading ? "Please wait..." : "Continue to Payment"}
                          </button>
                        </>
                      ) : (
                        <>
                          <p
                            style={{
                              color: landingSemantic.text.muted,
                              fontSize: "14px",
                              lineHeight: 1.6,
                              marginBottom: "16px",
                            }}
                          >
                            {paidSignupResponse ? (
                              <>Payment received for your <strong>{signupOrder.plan_name}</strong> workspace — starting setup.</>
                            ) : signupOrder.is_free || signupOrder.amount === 0 ? (
                              <>Confirm your free <strong>{signupOrder.plan_name}</strong> plan to start workspace provisioning.</>
                            ) : (
                              <>Complete payment to start provisioning your{" "}
                              <strong>{signupOrder.plan_name}</strong> workspace.{" "}
                              {signupOrder.gst_amount > 0 ? (
                                <>
                                  Total amount is {signupOrder.currency} {signupOrder.total_amount} ({signupOrder.currency} {signupOrder.price} + 18% GST) / {signupOrder.billing_cycle}.
                                </>
                              ) : (
                                <>
                                  Plan amount is {signupOrder.currency} {signupOrder.price} / {signupOrder.billing_cycle}.
                                </>
                              )}
                              </>
                            )}
                          </p>
                          <div
                            className="form-group"
                            style={{
                              background: landingSemantic.surface.header,
                              borderRadius: "10px",
                              padding: "14px 16px",
                              marginBottom: "16px",
                            }}
                          >
                            <div style={{ fontWeight: 700, fontSize: "18px" }}>
                              {signupOrder.currency} {signupOrder.gst_amount > 0 ? signupOrder.total_amount : signupOrder.price}
                              <span style={{ fontSize: "13px", fontWeight: 500, color: landingSemantic.text.muted }}>
                                {" "}/ {signupOrder.billing_cycle}
                              </span>
                            </div>
                            {signupOrder.gst_amount > 0 && (
                              <div style={{ fontSize: "12px", color: landingSemantic.text.muted, marginTop: "4px" }}>
                                {signupOrder.currency} {signupOrder.price} + 18% GST ({signupOrder.currency} {signupOrder.gst_amount})
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="form-submit"
                            onClick={
                              paidSignupResponse
                                ? () => onSignupPaymentSuccess(paidSignupResponse)
                                : handlePayForSignup
                            }
                            disabled={signUpLoading}
                            data-testid="landing-sign-up-pay-button"
                          >
                            {signUpLoading
                              ? "Processing..."
                              : paidSignupResponse
                              ? "Retry starting workspace setup"
                              : signupOrder.is_free || signupOrder.amount === 0
                              ? "Activate free plan"
                              : "Pay with Razorpay"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {/* <div className="form-toggle">
                    Already have an account?{" "}
                    <button
                      onClick={() => {
                        setShowSignUpModal(false);
                        setShowSignInModal(true);
                      }}
                      data-testid="landing-sign-up-switch-to-sign-in-button"
                    >
                      Sign In
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PROVISIONING SUCCESS MODAL */}
      {showProvisioningModal && (
        <div
          className="modal-overlay provisioning-overlay"
          onClick={closeProvisioningModal}
        >
          <div
            className="modal-content provisioning-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-provisioning-modal"
          >
            <button
              className="modal-close"
              onClick={closeProvisioningModal}
              aria-label="Close"
              data-testid="landing-provisioning-close-button"
            >
              ×
            </button>
            <div className="provisioning-checkmark-wrap">
              <svg
                className="provisioning-checkmark"
                viewBox="0 0 52 52"
                aria-hidden="true"
              >
                <circle
                  className="provisioning-checkmark-circle"
                  cx="26"
                  cy="26"
                  r="25"
                  fill="none"
                />
                <path
                  className="provisioning-checkmark-check"
                  fill="none"
                  d="M14.1 27.2l7.1 7.2 16.7-16.8"
                />
              </svg>
            </div>
            <h2 className="provisioning-title">
              {provisioningVariant === PROVISIONING_VARIANT.PAYMENT_SUCCESS
                ? "Payment received!"
                : "Workspace is being set up"}
            </h2>
            <p className="provisioning-message">
              {provisioningMessage ||
                (provisioningVariant === PROVISIONING_VARIANT.PAYMENT_SUCCESS ? (
                  <>
                    Your Nova workspace is being provisioned in the background.
                    This usually takes approximately{" "}
                    <strong>30 minutes</strong>.
                  </>
                ) : (
                  <>
                    Your Nova workspace is already being set up. This usually
                    takes approximately <strong>30 minutes</strong>. You can
                    sign in once setup finishes.
                  </>
                ))}
            </p>
            <p className="provisioning-message">
              You will receive an email at{" "}
              <strong>{provisioningEmail}</strong> when everything is ready.
              After that, sign in with the password you created during signup.
            </p>
            {provisioningDomain ? (
              <p className="provisioning-domain">
                Expected domain: <strong>https://{provisioningDomain}/</strong>
              </p>
            ) : null}
            <button
              type="button"
              className="auth-action primary provisioning-dismiss"
              onClick={() => {
                closeProvisioningModal();
                setShowSignInModal(true);
              }}
              data-testid="landing-provisioning-dismiss-button"
            >
              Go to sign in
            </button>
          </div>
        </div>
      )}

      {/* DEMO REQUEST MODAL */}
      {showDemoModal && (
        <div
          className="modal-overlay demo-overlay"
          onClick={() => {
            setShowDemoModal(false);
            setDemoFeedback({ type: "", message: "" });
          }}
        >
          <div
            className="modal-content demo-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="landing-demo-modal"
          >
            <div className="demo-modal-header">
              <button
                className="modal-close demo-close"
                onClick={() => {
                  setShowDemoModal(false);
                  setDemoFeedback({ type: "", message: "" });
                }}
                data-testid="landing-demo-close-button"
              >
                ×
              </button>
              <div className="demo-badge">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="4" y="3" width="16" height="18" rx="2" />
                  <line x1="8" y1="8" x2="16" y2="8" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                  <line x1="8" y1="16" x2="13" y2="16" />
                </svg>
                Book a Demo
              </div>
              <h2 className="modal-title">Let's show you NOVA in action</h2>
              <p className="modal-subtitle">
                Fill in your details and our team will reach out within 24 hours
                to schedule your personalised demo.
              </p>
            </div>
            <div className="demo-modal-body">
              {demoFeedback.message && (
                <div className={`demo-feedback ${demoFeedback.type}`} data-testid="landing-demo-feedback-banner">
                  {demoFeedback.message}
                </div>
              )}
              <form onSubmit={handleDemoSubmit} noValidate>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      className={`form-input ${demoErrors.fullName ? "input-error" : ""}`}
                      placeholder="John Doe"
                      value={demoForm.fullName}
                      onChange={handleDemoChange}
                      required
                      data-testid="landing-demo-full-name-input"
                    />
                    {demoErrors.fullName && (
                      <p className="form-error">{demoErrors.fullName}</p>
                    )}
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">
                      Work Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      name="workEmail"
                      className={`form-input ${demoErrors.workEmail ? "input-error" : ""}`}
                      placeholder="you@company.com"
                      value={demoForm.workEmail}
                      onChange={handleDemoChange}
                      required
                      data-testid="landing-demo-email-input"
                    />
                    {demoErrors.workEmail && (
                      <p className="form-error">{demoErrors.workEmail}</p>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      className={`form-input ${demoErrors.phoneNumber ? "input-error" : ""}`}
                      placeholder="+91 9876543210"
                      value={demoForm.phoneNumber}
                      onChange={handleDemoChange}
                      required
                      data-testid="landing-demo-phone-input"
                    />
                    {demoErrors.phoneNumber && (
                      <p className="form-error">{demoErrors.phoneNumber}</p>
                    )}
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">
                      Company Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      className={`form-input ${demoErrors.companyName ? "input-error" : ""}`}
                      placeholder="XYZ solutions"
                      value={demoForm.companyName}
                      onChange={handleDemoChange}
                      required
                      data-testid="landing-demo-company-name-input"
                    />
                    {demoErrors.companyName && (
                      <p className="form-error">{demoErrors.companyName}</p>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">
                      Your Role / Designation
                    </label>
                    <input
                      type="text"
                      name="role"
                      className="form-input"
                      placeholder="Head of Operations"
                      value={demoForm.role}
                      onChange={handleDemoChange}
                      data-testid="landing-demo-role-input"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">
                      Company Size <span className="required">*</span>
                    </label>
                    <select
                      name="companySize"
                      className={`form-input ${demoErrors.companySize ? "input-error" : ""}`}
                      value={demoForm.companySize}
                      onChange={handleDemoChange}
                      required
                      data-testid="landing-demo-company-size-input"
                    >
                      <option value="">Select team size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                    {demoErrors.companySize && (
                      <p className="form-error">{demoErrors.companySize}</p>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    What's the biggest challenge you're looking to solve?{" "}
                    <span style={{ color: landingSemantic.text.muted }}>(Optional)</span>
                  </label>
                  <textarea
                    name="challenge"
                    className="form-input"
                    placeholder="e.g. Our team spends too much time searching for answers scattered across documents, tools, and past conversations..."
                    value={demoForm.challenge}
                    onChange={handleDemoChange}
                    maxLength="800"
                    rows="4"
                    data-testid="landing-demo-challenge-input"
                  />
                  <p className="char-count">
                    {demoForm.challenge.length} / 800
                  </p>
                </div>
                <button
                  type="submit"
                  className="form-submit demo-submit"
                  disabled={demoSubmitting}
                  data-testid="landing-demo-submit-button"
                >
                  {demoSubmitting ? "Submitting..." : "Book My Demo"}
                </button>
              </form>
              <p className="form-notice">
                🔒 Your details are kept private and never shared with third
                parties.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
